import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    Divider,
    Grid,
    Avatar,
    Stack,
    useTheme, // Keep for transitions
    // useMediaQuery // Not strictly needed now as MainDrawer is always permanent
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// --- MODIFIED IMPORT: Import MainDrawer AND its exported width constants ---
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed

// --- Placeholders & Colors (Same as your original file) ---
const CHECKOV_LOGO_PATH = "/assets/checkov.svg";
const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg";
const FAILED_ICON_PATH = "/assets/close-circle-fill.svg";
const CHIP_COLOR_GREEN = '#5CB85C';
const CHIP_COLOR_RED = '#D9534F';

// --- Content Component (CheckovDetailsContent) ---
// This is your original CheckovDetails component, renamed.
// NO UI changes inside this component itself.
const CheckovDetailsContent: React.FC = () => {
    const navigate = useNavigate();
    const [allCheckovData, setAllCheckovData] = useState<any[]>([]);
    const [groupedData, setGroupedData] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
    const [expandedScanId, setExpandedScanId] = useState<string | null>(null);
    const getCheckovResultsQuery = `
        query GetCheckovResults {
          getCheckovResults {
            id
            resourceId
            resourceType
            passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
            failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
            summary { checkovVersion failed passed parsingErrors resourceCount skipped }
            createdAt
            updatedAt
            application { applicationName }
            service { serviceName }
            cluster { clusterName }
          }
        }
    `;

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            setAllCheckovData([]);
            setGroupedData({});
            setExpandedAccordion(false);
            setExpandedScanId(null);
            try {
                const response = await queryGraphql(getCheckovResultsQuery);
                const fetchedData = response?.getCheckovResults || [];
                if (Array.isArray(fetchedData)) {
                     if (fetchedData.length === 0) {
                         setAllCheckovData([]); setGroupedData({});
                     } else {
                        setAllCheckovData(fetchedData);
                        const groups = fetchedData.reduce((acc, result) => {
                            const type = result?.resourceType;
                            if (type && result) {
                                if (!acc[type]) { acc[type] = []; }
                                acc[type]!.push(result);
                                acc[type]?.sort((a: any, b: any) => {
                                    let timeA = 0, timeB = 0;
                                    const tsA = a?.updatedAt, tsB = b?.updatedAt;
                                    try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}
                                    try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}
                                    return timeB - timeA;
                                });
                            }
                            return acc;
                        }, {} as any);
                        setGroupedData(groups);
                     }
                } else if (response && response.errors) { setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`); }
                 else { setError("Could not fetch Checkov data or the format is incorrect."); }
            } catch (err: any) { setError(err.message || "An unexpected error occurred."); }
            finally { setIsLoading(false); }
        };
        fetchData();
    }, []); // Empty dependency array for fetch on mount

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordion(isExpanded ? panel : false);
        if (!isExpanded || (isExpanded && panel !== expandedAccordion)) { setExpandedScanId(null); }
    };
    const handleScanSelect = (scanId: string | undefined | null) => {
         if (!scanId) return;
        setExpandedScanId(prevId => (prevId === scanId ? null : scanId));
    };
    const formatDate = (timestamp: string | number | null | undefined): string => {
        if (timestamp === null || timestamp === undefined) return "N/A";
        let date: Date | null = null;
        try {
             if (typeof timestamp === 'number') { date = new Date(timestamp); }
             else if (typeof timestamp === 'string') {
                 const nTs = Number(timestamp);
                 if (!isNaN(nTs) && timestamp.length > 5) { date = new Date(nTs); } else { date = new Date(timestamp); }
             }
             if (date && !isNaN(date.getTime())) { return date.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit'}); }
             else { return "Invalid Date"; }
        } catch (e) { return "Invalid Date"; }
    };
    const formatHeaderDate = (dateObj: Date | null): string => {
        if (!dateObj) return "N/A";
         try { if (!isNaN(dateObj.getTime())) { return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } else { return "Invalid Date"; } }
         catch (e) { return "Invalid Date"; }
    };
    const getResourceDisplayName = (scan: any): string => {
        const type = scan?.resourceType; const resourceId = scan?.resourceId ?? 'Unknown ID';
        switch (type) {
            case 'application': return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
            case 'cluster': return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
            case 'service': return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
            default: return resourceId ?? 'Unknown Resource';
        }
    };
    const { overallLatestScanDate } = useMemo(() => {
        let latestDateObj: Date | null = null;
        allCheckovData.forEach(scan => {
            const timestamp = scan?.updatedAt;
             if (timestamp !== null && timestamp !== undefined) {
                 try {
                     let scanDate: Date | null = null;
                     if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
                     else if (typeof timestamp === 'string') {
                         const nTs = Number(timestamp);
                         if (!isNaN(nTs) && timestamp.length > 5) { scanDate = new Date(nTs); } else { scanDate = new Date(timestamp); }
                     }
                     if (scanDate && !isNaN(scanDate.getTime())) {
                         if (!latestDateObj || scanDate > latestDateObj) {
                             latestDateObj = scanDate;
                         }
                     }
                 } catch(e) { /* Ignore date parse errors */ }
            }
        });
        return { overallLatestScanDate: latestDateObj };
    }, [allCheckovData]);
    return (
        // The <MainDrawer/> call is removed from here
        <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, minHeight: "100vh" }} >
            {/* Header */}
             <Box sx={{ mb: 3 }}>
                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
                 </Breadcrumbs>
                 <Box sx={{display:'flex', justifyContent:'space-between' }}> 
                 <Box display="flex" alignItems="center" >
                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
                 </Box>
                                             <IconButton
                                                      aria-label="Help" 
                                                      onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                      sx={{
                                                        padding: 0, 
                                                        border: 'none',  
                                                        ml:2,
                                                        '&:hover': {
                                                          
                                                          backgroundColor: 'action.hover', 
                                                        },
                                                        
                                                      }}
                                                    >
                                                      <Box
                                                        component="img"
                                                        src="/assets/Help.svg" 
                                                        alt="" 
                                                        sx={{
                                                          width: 40,  
                                                          height: 40,
                                                          display: 'block', 
                                                        }}
                                                      />
                                                    </IconButton>
                 </Box>
             </Box>

            {/* Content Area - No changes to internal structure of this section */}
            {/* The outer Box with maxWidth:'lg' from original code is also implicitly handled by this structure */}
            <>
                {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
                {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
                {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

                {!isLoading && !error && allCheckovData.length > 0 && (
                    <>
                        <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
                        <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
                             <Grid container spacing={2} alignItems="center">
                                 <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar> </Grid>
                                 <Grid item xs={12} sm>
                                     <Box display="flex" alignItems="center" mb={0.5} >
                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
                                     </Box>
                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
                                 </Grid>
                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
                             </Grid>
                        </Paper>

                        <Box>
                             {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
                            {(['cluster', 'application', 'service'] as const).map((resourceType) => {
                                const scans = groupedData[resourceType];
                                if (!Array.isArray(scans) || scans.length === 0) return null;
                                const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';
                                return (
                                    <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
                                            <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
                                            <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
                                            <List dense disablePadding>
                                                {scans.map((scan: any, index: number) => {
                                                    const isExpanded = expandedScanId === scan?.id;
                                                    const passedCount = scan?.summary?.passed ?? 0;
                                                    const failedCount = scan?.summary?.failed ?? 0;
                                                    return (
                                                        <React.Fragment key={scan?.id ?? `scan-${index}`}>
                                                            <ListItemButton
                                                                selected={isExpanded}
                                                                onClick={() => handleScanSelect(scan?.id)}
                                                                sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, borderBottom: !isExpanded && index < scans.length - 1 ? '1px solid #eee' : 'none' }}
                                                            >
                                                                <ListItemText
                                                                    primary={getResourceDisplayName(scan)}
                                                                    secondary={`Scanned: ${formatDate(scan?.updatedAt)}`}
                                                                    primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isExpanded ? 'primary.main' : 'text.primary' }}
                                                                    secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
                                                                />
                                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2, flexShrink: 0, fontFamily:'Gilroy-SemiBold' }}>
                                                                    <Tooltip title={`Passed checks: ${passedCount}`} placement="top">
                                                                        <Chip
                                                                            label={`Passed Checks: ${passedCount}`}
                                                                            icon={ <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}> <img src={PASSED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }} /> </Avatar> }
                                                                            size="small"
                                                                            sx={{ bgcolor: CHIP_COLOR_GREEN, color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 }, '& .MuiChip-label': { pl: '8px', pr: '12px' } }}
                                                                        />
                                                                    </Tooltip>
                                                                    <Tooltip title={`Failed checks: ${failedCount}`} placement="top">
                                                                        <Chip
                                                                            label={`Failed Checks: ${failedCount}`}
                                                                            icon={ <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}> <img src={FAILED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }}/> </Avatar> }
                                                                            size="small"
                                                                            sx={{ bgcolor: CHIP_COLOR_RED, color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 }, '& .MuiChip-label': { pl: '8px', pr: '12px' } }}
                                                                        />
                                                                    </Tooltip>
                                                                </Stack>
                                                            </ListItemButton>
                                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                 <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: '1px solid #eee' }}>
                                                                    <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {getResourceDisplayName(scan)} </Typography>
                                                                    <Box mb={3}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
                                                                            <Avatar sx={{ width: 20, height: 20, bgcolor: CHIP_COLOR_GREEN }}> <img src={PASSED_ICON_PATH} alt="Passed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/> </Avatar>
                                                                            <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks </Typography>
                                                                            <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker" sx={{ ml: 0.5 }}> ({scan?.passedChecks?.length ?? 0}) </Typography>
                                                                        </Box>
                                                                        <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
                                                                            {(scan?.passedChecks?.length ?? 0) > 0 ? (
                                                                                scan.passedChecks.map((check: any, checkIndex: number) => (
                                                                                    <ListItem key={`pass-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.passedChecks.length - 1)}>
                                                                                        <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
                                                                                    </ListItem>
                                                                                ))
                                                                            ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
                                                                        </List>
                                                                    </Box>
                                                                    <Box>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
                                                                            <Avatar sx={{ width: 20, height: 20, bgcolor: CHIP_COLOR_RED }}> <img src={FAILED_ICON_PATH} alt="Failed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/> </Avatar>
                                                                            <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks </Typography>
                                                                            <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker" sx={{ ml: 0.5 }}> ({scan?.failedChecks?.length ?? 0}) </Typography>
                                                                        </Box>
                                                                        <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
                                                                            {(scan?.failedChecks?.length ?? 0) > 0 ? (
                                                                                scan.failedChecks.map((check: any, checkIndex: number) => (
                                                                                    <ListItem key={`fail-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
                                                                                        <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
                                                                                        {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
                                                                                    </ListItem>
                                                                                ))
                                                                            ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
                                                                        </List>
                                                                    </Box>
                                                                </Box>
                                                            </Collapse>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Box>
                    </>
                )}
            </>
        </Container>
    );
};


// --- Parent Layout Component (CheckovDetailsPage) ---
// This component now uses the state reported by MainDrawer
const CheckovDetails: React.FC = () => {
    const theme = useTheme(); // Keep theme for transitions

    // State to store the drawer's actual open status, reported by MainDrawer
    // Initialize based on typical MainDrawer default (true) or what MainDrawer reports first
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(true);

    // Callback function passed to MainDrawer to update parent's state
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Calculate marginLeft for main content based on reported drawer state
    // Uses the imported width constants from MainDrawer.tsx
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth-248 : collapsedDrawerWidth-70;

    return (
        // Root Box with display: flex
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* CssBaseline can be useful for global styles, but removing if not strictly needed for this page */}
            {/* <CssBaseline /> */}

            {/* Render YOUR MainDrawer */}
            {/* Pass the 'index' prop and the 'onStateChange' callback */}
            <MainDrawer
                index={"integrations"}
                onStateChange={handleDrawerStateChange}
            />

            {/* Main Content Box */}
            {/* This Box wraps CheckovDetailsContent and handles shifting */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    //bgcolor: '#fafafa', // Original background
                    p: 0, // Padding is handled by the inner Container of CheckovDetailsContent
                    transition: theme.transitions.create('margin', { // Transition margin changes
                        easing: theme.transitions.easing.sharp,
                        // Match drawer's duration if possible
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: `${currentMarginLeft}vh`, // Apply margin based on actual drawer width
                }}
            >
                <CheckovDetailsContent />
            </Box>
        </Box>
    );
};

// Export the parent layout component
export default CheckovDetails;


// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Placeholder for Logos ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Your actual path
// // --- Define paths for custom SVG icons ---
// const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg"; // Your actual path
// const FAILED_ICON_PATH = "/assets/close-circle-fill.svg"; // Your actual path

// // --- CHANGE: Define specific colors based on the image analysis ---
// const CHIP_COLOR_GREEN = '#5CB85C'; // Muted green from image
// const CHIP_COLOR_RED = '#D9534F';   // Muted red from image


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [expandedScanId, setExpandedScanId] = useState<string | null>(null);

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             application { applicationName }
//             service { serviceName }
//             cluster { clusterName }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedScanId(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData = response?.getCheckovResults || [];
//                 if (Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 acc[type]?.sort((a: any, b: any) => { // Sort by date
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}
//                                     return timeB - timeA; // Descending
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                      }
//                 } else if (response && response.errors) { setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`); }
//                  else { setError("Could not fetch Checkov data or the format is incorrect."); }
//             } catch (err: any) { setError(err.message || "An unexpected error occurred."); }
//             finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) { setExpandedScanId(null); }
//     };
//     const handleScanSelect = (scanId: string | undefined | null) => {
//          if (!scanId) return;
//         setExpandedScanId(prevId => (prevId === scanId ? null : scanId));
//     };

//     // --- Helper Functions ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//              if (typeof timestamp === 'number') { date = new Date(timestamp); }
//              else if (typeof timestamp === 'string') {
//                  const nTs = Number(timestamp);
//                  if (!isNaN(nTs) && timestamp.length > 5) { date = new Date(nTs); } else { date = new Date(timestamp); }
//              }
//              if (date && !isNaN(date.getTime())) { return date.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit'}); }
//              else { return "Invalid Date"; }
//         } catch (e) { return "Invalid Date"; }
//     };
//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try { if (!isNaN(dateObj.getTime())) { return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } else { return "Invalid Date"; } }
//          catch (e) { return "Invalid Date"; }
//     }
//     const getResourceDisplayName = (scan: any): string => {
//         const type = scan?.resourceType; const resourceId = scan?.resourceId ?? 'Unknown ID';
//         switch (type) {
//             case 'application': return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
//             case 'cluster': return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
//             case 'service': return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
//             default: return resourceId ?? 'Unknown Resource';
//         }
//     };
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//              if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                          if (!isNaN(nTs) && timestamp.length > 5) { scanDate = new Date(nTs); } else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) { if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; } }
//                  } catch(e) {}
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]);
//     // --- End Helper Functions ---


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//              <Box sx={{ mb: 3 }}>
//                  <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                  </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                      <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                      <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                              <Grid container spacing={2} alignItems="center">
//                                  <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar> </Grid>
//                                  <Grid item xs={12} sm>
//                                      <Box display="flex" alignItems="center" mb={0.5} >
//                                          <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                          <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                      </Box>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                  </Grid>
//                                  <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                              </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['cluster', 'application', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!Array.isArray(scans) || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             <List dense disablePadding>
//                                                 {scans.map((scan: any, index: number) => {
//                                                     const isExpanded = expandedScanId === scan?.id;
//                                                     const passedCount = scan?.summary?.passed ?? 0;
//                                                     const failedCount = scan?.summary?.failed ?? 0;

//                                                     return (
//                                                         <React.Fragment key={scan?.id ?? `scan-${index}`}>
//                                                             <ListItemButton
//                                                                 selected={isExpanded}
//                                                                 onClick={() => handleScanSelect(scan?.id)}
//                                                                 sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, borderBottom: !isExpanded && index < scans.length - 1 ? '1px solid #eee' : 'none' }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={getResourceDisplayName(scan)}
//                                                                     secondary={`Scanned: ${formatDate(scan?.updatedAt)}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 {/* Chips in List Item */}
//                                                                 <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2, flexShrink: 0, fontFamily:'Gilroy-SemiBold' }}>
//                                                                     <Tooltip title={`Passed checks: ${passedCount}`} placement="top">
//                                                                         <Chip
//                                                                             label={`Passed Checks: ${passedCount}`}
//                                                                             icon={
//                                                                                  <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}>
//                                                                                      {/* Apply filter to img for white icon */}
//                                                                                      <img src={PASSED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }} />
//                                                                                  </Avatar>
//                                                                             }
//                                                                             size="small"
//                                                                             sx={{
//                                                                                 // --- CHANGE: Use specific green color ---
//                                                                                 bgcolor: CHIP_COLOR_GREEN,
//                                                                                 color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500,
//                                                                                 '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 },
//                                                                                 '& .MuiChip-label': { pl: '8px', pr: '12px' }
//                                                                             }}
//                                                                         />
//                                                                     </Tooltip>
//                                                                     <Tooltip title={`Failed checks: ${failedCount}`} placement="top">
//                                                                         <Chip
//                                                                             label={`Failed Checks: ${failedCount}`}
//                                                                             icon={
//                                                                                 <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}>
//                                                                                      {/* Apply filter to img for white icon */}
//                                                                                     <img src={FAILED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                                 </Avatar>
//                                                                             }
//                                                                             size="small"
//                                                                             sx={{
//                                                                                 // --- CHANGE: Use specific red color ---
//                                                                                 bgcolor: CHIP_COLOR_RED,
//                                                                                 color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500,
//                                                                                 '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 },
//                                                                                 '& .MuiChip-label': { pl: '8px', pr: '12px' }
//                                                                             }}
//                                                                         />
//                                                                     </Tooltip>
//                                                                 </Stack>
//                                                             </ListItemButton>

//                                                             {/* Collapsible Detail Section */}
//                                                             <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//                                                                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: '1px solid #eee' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {getResourceDisplayName(scan)} </Typography>
//                                                                     {/* Passed Checks Detail Section */}
//                                                                     <Box mb={3}>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* Icon + Text for Detail Header */}
//                                                                             {/* --- CHANGE: Use specific green color --- */}
//                                                                             <Avatar sx={{ width: 20, height: 20, bgcolor: CHIP_COLOR_GREEN }}>
//                                                                                  <img src={PASSED_ICON_PATH} alt="Passed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                             </Avatar>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks </Typography>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker" sx={{ ml: 0.5 }}> ({scan?.passedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.passedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.passedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`pass-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.passedChecks.length - 1)}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                     {/* Failed Checks Detail Section */}
//                                                                     <Box>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* Icon + Text for Detail Header */}
//                                                                              {/* --- CHANGE: Use specific red color --- */}
//                                                                             <Avatar sx={{ width: 20, height: 20, bgcolor: CHIP_COLOR_RED }}>
//                                                                                 <img src={FAILED_ICON_PATH} alt="Failed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                             </Avatar>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks </Typography>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker" sx={{ ml: 0.5 }}> ({scan?.failedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.failedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.failedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`fail-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                         {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar, // Keep Avatar import
//     Stack, // Keep Stack import
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Placeholder for Logos ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Your actual path
// // --- Define paths for custom SVG icons ---
// const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg"; // Your actual path
// const FAILED_ICON_PATH = "/assets/close-circle-fill (1).svg"; // Your actual path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [expandedScanId, setExpandedScanId] = useState<string | null>(null);

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             application { applicationName }
//             service { serviceName }
//             cluster { clusterName }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedScanId(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData = response?.getCheckovResults || [];
//                 if (Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort scans within each group by date (newest first)
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     // Robust date parsing for sorting
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}
//                                     return timeB - timeA; // Descending order
//                                 });
//                             }
//                             return acc;
//                         }, {} as any); // Initialize accumulator as any
//                         setGroupedData(groups);
//                      }
//                 } else if (response && response.errors) {
//                     console.error("GraphQL Errors:", response.errors);
//                     setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 }
//                  else {
//                     console.error("Checkov data not found or is not an array:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                  }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Empty dependency array means this runs once on mount

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         // Collapse any open scan item when its parent accordion changes state
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//             setExpandedScanId(null);
//         }
//     };

//     const handleScanSelect = (scanId: string | undefined | null) => {
//          if (!scanId) return; // Ignore if scanId is invalid
//         // Toggle the expansion state of the clicked scan item
//         setExpandedScanId(prevId => (prevId === scanId ? null : scanId));
//     };

//     // --- Helper Functions ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//              // Handle both numeric (milliseconds) and string timestamps
//              if (typeof timestamp === 'number') {
//                  date = new Date(timestamp);
//              } else if (typeof timestamp === 'string') {
//                  const nTs = Number(timestamp); // Try converting string to number
//                  if (!isNaN(nTs) && timestamp.length > 5) { // Check if it looks like a timestamp
//                      date = new Date(nTs);
//                  } else { // Otherwise, parse as a date string (e.g., ISO)
//                      date = new Date(timestamp);
//                  }
//              }
//              // Check if date is valid and format it
//              if (date && !isNaN(date.getTime())) {
//                  return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//              } else {
//                  console.warn("Could not parse date:", timestamp);
//                  return "Invalid Date";
//              }
//         } catch (e) {
//             console.error("Error formatting date:", e, timestamp);
//             return "Invalid Date";
//         }
//     };

//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try {
//              // Check if date is valid before formatting
//              if (!isNaN(dateObj.getTime())) {
//                  return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//              } else {
//                  return "Invalid Date";
//              }
//         } catch (e) {
//             console.error("Error formatting header date:", e);
//             return "Invalid Date";
//         }
//     }

//     const getResourceDisplayName = (scan: any): string => {
//         // Use optional chaining and nullish coalescing for safety
//         const type = scan?.resourceType;
//         const resourceId = scan?.resourceId ?? 'Unknown ID'; // Default if resourceId is missing
//         switch (type) {
//             case 'application': return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
//             case 'cluster': return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
//             case 'service': return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
//             default: return resourceId ?? 'Unknown Resource'; // Fallback
//         }
//     };

//     // Calculate the latest scan date across all fetched data
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const timestamp = scan?.updatedAt; // Use optional chaining
//              if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      // Robust date creation logic
//                      if (typeof timestamp === 'number') {
//                          scanDate = new Date(timestamp);
//                      } else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                          if (!isNaN(nTs) && timestamp.length > 5) { scanDate = new Date(nTs); }
//                          else { scanDate = new Date(timestamp); }
//                      }
//                      // Check if valid and update latest date if newer
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) {
//                              latestDateObj = scanDate;
//                          }
//                      } else {
//                          console.warn("Could not parse scan date for latest date calculation:", timestamp);
//                      }
//                  } catch(e) {
//                     console.error("Error processing scan date for latest date calculation:", timestamp, e);
//                  }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]); // Recalculate only when allCheckovData changes
//     // --- End Helper Functions ---


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//              <Box sx={{ mb: 3 }}>
//                  <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                  </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                      <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                      <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                              <Grid container spacing={2} alignItems="center">
//                                  <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar> </Grid>
//                                  <Grid item xs={12} sm>
//                                      <Box display="flex" alignItems="center" mb={0.5} >
//                                          <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                          <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                      </Box>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                  </Grid>
//                                  <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                              </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!Array.isArray(scans) || scans.length === 0) return null; // Skip if no scans for this type
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion
//                                         key={resourceType}
//                                         expanded={expandedAccordion === resourceType}
//                                         onChange={handleAccordionChange(resourceType)}
//                                         disableGutters
//                                         elevation={1}
//                                         sx={{
//                                             border: "1px solid #E0E0E0",
//                                             borderRadius: "8px !important", // Override default Accordion radius
//                                             boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
//                                             '&:before': { display: 'none' }, // Remove default top border
//                                             mb: 2.5,
//                                             overflow: 'hidden', // Ensure children adhere to border radius
//                                         }}
//                                     >
//                                         <AccordionSummary
//                                             expandIcon={<ExpandMoreIcon />}
//                                             aria-controls={`${resourceType}-content`}
//                                             id={`${resourceType}-header`}
//                                             sx={{
//                                                 bgcolor: 'background.paper',
//                                                 minHeight: '56px', // Standard height
//                                                 px: { xs: 2, md: 3 },
//                                                 py: 1,
//                                                 '& .MuiAccordionSummary-content': { alignItems: 'center' } // Vertically align content
//                                             }}
//                                         >
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans for the current resource type */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan: any, index: number) => {
//                                                     const isExpanded = expandedScanId === scan?.id;
//                                                     const passedCount = scan?.summary?.passed ?? 0;
//                                                     const failedCount = scan?.summary?.failed ?? 0;

//                                                     return (
//                                                         <React.Fragment key={scan?.id ?? `scan-${index}`}> {/* Use Fragment for grouping */}
//                                                             <ListItemButton
//                                                                 selected={isExpanded} // Highlight if details are open
//                                                                 onClick={() => handleScanSelect(scan?.id)} // Toggle details on click
//                                                                 sx={{
//                                                                     '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } },
//                                                                     px: { xs: 2, md: 3 }, py: 2,
//                                                                     // Add divider only if not expanded and not the last item
//                                                                     borderBottom: !isExpanded && index < scans.length - 1 ? '1px solid #eee' : 'none'
//                                                                 }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={getResourceDisplayName(scan)}
//                                                                     secondary={`Scanned: ${formatDate(scan?.updatedAt)}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 {/* Chips Displayed in the List Item */}
//                                                                 <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2, flexShrink: 0 }}>
//                                                                     <Tooltip title={`Passed checks: ${passedCount}`} placement="top">
//                                                                         <Chip
//                                                                             label={`Passed Checks: ${passedCount}`}
//                                                                             icon={ // Circular icon with white symbol
//                                                                                  <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}>
//                                                                                      <img src={PASSED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }} />
//                                                                                  </Avatar>
//                                                                             }
//                                                                             size="small"
//                                                                             sx={{
//                                                                                 bgcolor: 'success.main', color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500,
//                                                                                 '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 }, // Style icon container
//                                                                                 '& .MuiChip-label': { pl: '8px', pr: '12px' }
//                                                                             }}
//                                                                         />
//                                                                     </Tooltip>
//                                                                     <Tooltip title={`Failed checks: ${failedCount}`} placement="top">
//                                                                         <Chip
//                                                                             label={`Failed Checks: ${failedCount}`}
//                                                                             icon={ // Circular icon with white symbol
//                                                                                 <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}>
//                                                                                     <img src={FAILED_ICON_PATH} alt="" width="18" height="18" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                                 </Avatar>
//                                                                             }
//                                                                             size="small"
//                                                                             sx={{
//                                                                                 bgcolor: 'error.main', color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500,
//                                                                                 '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-2px', width: 18, height: 18 }, // Style icon container
//                                                                                 '& .MuiChip-label': { pl: '8px', pr: '12px' }
//                                                                             }}
//                                                                         />
//                                                                     </Tooltip>
//                                                                 </Stack>
//                                                             </ListItemButton>

//                                                             {/* Collapsible Detail Section */}
//                                                             <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//                                                                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: '1px solid #eee' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}>
//                                                                         Details for Scan: {getResourceDisplayName(scan)}
//                                                                     </Typography>
//                                                                     {/* Passed Checks Detail Section */}
//                                                                     <Box mb={3}>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* Icon + Text for Detail Header */}
//                                                                             <Avatar sx={{ width: 20, height: 20, bgcolor: 'success.main' }}>
//                                                                                  <img src={PASSED_ICON_PATH} alt="Passed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                             </Avatar>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks </Typography>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker" sx={{ ml: 0.5 }}> ({scan?.passedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.passedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.passedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`pass-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.passedChecks.length - 1)}>
//                                                                                         <ListItemText
//                                                                                             primary={check?.checkId ?? 'Unknown ID'}
//                                                                                             secondary={check?.checkName || check?.description || 'No description available'}
//                                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }}
//                                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                                         />
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : (
//                                                                                 <ListItem>
//                                                                                     <ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} />
//                                                                                 </ListItem>
//                                                                             )}
//                                                                         </List>
//                                                                     </Box>
//                                                                     {/* Failed Checks Detail Section */}
//                                                                     <Box>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* Icon + Text for Detail Header */}
//                                                                             <Avatar sx={{ width: 20, height: 20, bgcolor: 'error.main' }}>
//                                                                                 <img src={FAILED_ICON_PATH} alt="Failed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                             </Avatar>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks </Typography>
//                                                                             <Typography component="span" fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker" sx={{ ml: 0.5 }}> ({scan?.failedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.failedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.failedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`fail-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
//                                                                                         <ListItemText
//                                                                                             primary={check?.checkId ?? 'Unknown ID'}
//                                                                                             secondary={check?.checkName || check?.description || 'No description available'}
//                                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }}
//                                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                                         />
//                                                                                         {/* Severity Chip (remains unchanged) */}
//                                                                                         {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : (
//                                                                                 <ListItem>
//                                                                                     <ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} />
//                                                                                 </ListItem>
//                                                                             )}
//                                                                         </List>
//                                                                     </Box>
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack, // --- CHANGE: Import Stack for layout ---
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Placeholder for Logos ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg";
// // --- Define paths for custom SVG icons ---
// const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg"; // Your actual path
// const FAILED_ICON_PATH = "/assets/close-circle-fill (1).svg"; // Your actual path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables (Unchanged) ---
//     const [allCheckovData, setAllCheckovData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [expandedScanId, setExpandedScanId] = useState<string | null>(null);

//     // --- GraphQL Query (Unchanged) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             application { applicationName }
//             service { serviceName }
//             cluster { clusterName }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect (Unchanged) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedScanId(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData = response?.getCheckovResults || [];
//                 if (Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}
//                                     return timeB - timeA;
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                      }
//                 } else if (response && response.errors) { setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`); }
//                  else { setError("Could not fetch Checkov data or the format is incorrect."); }
//             } catch (err: any) { setError(err.message || "An unexpected error occurred."); }
//             finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // --- Event Handlers (Unchanged) ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) { setExpandedScanId(null); }
//     };
//     const handleScanSelect = (scanId: string | undefined | null) => {
//          if (!scanId) return;
//         setExpandedScanId(prevId => (prevId === scanId ? null : scanId));
//     };

//     // --- Helper Functions (Unchanged) ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//              if (typeof timestamp === 'number') { date = new Date(timestamp); }
//              else if (typeof timestamp === 'string') {
//                  const nTs = Number(timestamp);
//                  if (!isNaN(nTs) && timestamp.length > 5) { date = new Date(nTs); } else { date = new Date(timestamp); }
//              }
//              if (date && !isNaN(date.getTime())) { return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
//              else { return "Invalid Date"; }
//         } catch (e) { return "Invalid Date"; }
//     };
//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try { if (!isNaN(dateObj.getTime())) { return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } else { return "Invalid Date"; } }
//          catch (e) { return "Invalid Date"; }
//     }
//     const getResourceDisplayName = (scan: any): string => {
//         const type = scan?.resourceType; const resourceId = scan?.resourceId ?? 'Unknown ID';
//         switch (type) {
//             case 'application': return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
//             case 'cluster': return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
//             case 'service': return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
//             default: return resourceId ?? 'Unknown Resource';
//         }
//     };
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//              if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                          if (!isNaN(nTs) && timestamp.length > 5) { scanDate = new Date(nTs); } else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) { if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; } }
//                  } catch(e) {}
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]);
//     // --- End Helper Functions ---


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//              <Box sx={{ mb: 3 }}>
//                  {/* ... Breadcrumbs and Title ... */}
//                  <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                  </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                      <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                      <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                              {/* ... Grid layout ... */}
//                              <Grid container spacing={2} alignItems="center">
//                                  <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar> </Grid>
//                                  <Grid item xs={12} sm>
//                                      <Box display="flex" alignItems="center" mb={0.5} >
//                                          <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                          <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                      </Box>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                  </Grid>
//                                  <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                              </Grid>
//                         </Paper>

//                         {/* Accordions */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!Array.isArray(scans) || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             <List dense disablePadding>
//                                                 {scans.map((scan: any, index: number) => {
//                                                     const isExpanded = expandedScanId === scan?.id;
//                                                     const passedCount = scan?.summary?.passed ?? 0;
//                                                     const failedCount = scan?.summary?.failed ?? 0;

//                                                     return (
//                                                         <React.Fragment key={scan?.id ?? `scan-${index}`}>
//                                                             <ListItemButton
//                                                                 selected={isExpanded}
//                                                                 onClick={() => handleScanSelect(scan?.id)}
//                                                                 sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, borderBottom: !isExpanded && index < scans.length - 1 ? '1px solid #eee' : 'none' }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={getResourceDisplayName(scan)}
//                                                                     secondary={`Scanned: ${formatDate(scan?.updatedAt)}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 {/* --- CHANGE: Layout for Chips + Numbers --- */}
//                                                                 <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 2, flexShrink: 0 }}>
//                                                                     {/* Passed Chip + Number */}
//                                                                     <Stack direction="row" spacing={0.75} alignItems="center">
//                                                                         <Tooltip title="Passed checks" placement="top">
//                                                                             <Chip
//                                                                                 label="Passed"
//                                                                                 // Use icon prop with white-filtered SVG
//                                                                                 icon={<img src={PASSED_ICON_PATH} alt="" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>}
//                                                                                 size="small"
//                                                                                 sx={{
//                                                                                     bgcolor: 'success.main', // Solid green background
//                                                                                     color: '#fff', // White text & icon
//                                                                                     height: '28px',
//                                                                                     borderRadius: '16px',
//                                                                                     fontSize: '0.8rem',
//                                                                                     fontWeight: 500,
//                                                                                     '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-4px' }, // Ensure icon is white, adjust spacing
//                                                                                     '& .MuiChip-label': { px: '8px'}
//                                                                                 }}
//                                                                             />
//                                                                         </Tooltip>
//                                                                         <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
//                                                                             {passedCount}
//                                                                         </Typography>
//                                                                     </Stack>

//                                                                      {/* Failed Chip + Number */}
//                                                                     <Stack direction="row" spacing={0.75} alignItems="center">
//                                                                         <Tooltip title="Failed checks" placement="top">
//                                                                              <Chip
//                                                                                 label="Failed"
//                                                                                  // Use icon prop with white-filtered SVG
//                                                                                 icon={<img src={FAILED_ICON_PATH} alt="" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }} />}
//                                                                                 size="small"
//                                                                                 sx={{
//                                                                                     bgcolor: 'error.main', // Solid red background
//                                                                                     color: '#fff', // White text & icon
//                                                                                     height: '28px',
//                                                                                     borderRadius: '16px',
//                                                                                     fontSize: '0.8rem',
//                                                                                     fontWeight: 500,
//                                                                                     '& .MuiChip-icon': { color: '#fff', ml: '6px', mr: '-4px' }, // Ensure icon is white, adjust spacing
//                                                                                     '& .MuiChip-label': { px: '8px' }
//                                                                                 }}
//                                                                             />
//                                                                         </Tooltip>
//                                                                         <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
//                                                                             {failedCount}
//                                                                         </Typography>
//                                                                     </Stack>
//                                                                 </Stack>
//                                                                 {/* --- END CHANGE --- */}
//                                                             </ListItemButton>

//                                                             <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//                                                                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: '1px solid #eee' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {getResourceDisplayName(scan)} </Typography>
//                                                                     {/* Passed Checks Section */}
//                                                                     <Box mb={3}>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* --- CHANGE: Use same Chip+Number style in header --- */}
//                                                                             <Chip
//                                                                                 label="Passed Checks"
//                                                                                 icon={<img src={PASSED_ICON_PATH} alt="" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>}
//                                                                                 size="small"
//                                                                                 sx={{ bgcolor: 'success.main', color: '#fff', '& .MuiChip-icon': { color: '#fff', ml: 1 } }}
//                                                                             />
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> ({scan?.passedChecks?.length ?? 0}) </Typography>
//                                                                              {/* --- END CHANGE --- */}
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.passedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.passedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`pass-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.passedChecks.length - 1)}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                     {/* Failed Checks Section */}
//                                                                     <Box>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                               {/* --- CHANGE: Use same Chip+Number style in header --- */}
//                                                                              <Chip
//                                                                                 label="Failed Checks"
//                                                                                 icon={<img src={FAILED_ICON_PATH} alt="" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }} />}
//                                                                                 size="small"
//                                                                                 sx={{ bgcolor: 'error.main', color: '#fff', '& .MuiChip-icon': { color: '#fff', ml: 1 } }}
//                                                                             />
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> ({scan?.failedChecks?.length ?? 0}) </Typography>
//                                                                              {/* --- END CHANGE --- */}
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.failedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.failedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`fail-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                         {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar, // Keep Avatar import
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Placeholder for Logos ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg";
// // --- Define paths for custom SVG icons ---
// const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg"; // Your actual path
// const FAILED_ICON_PATH = "/assets/close-circle-fill (1).svg"; // Your actual path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables (Unchanged) ---
//     const [allCheckovData, setAllCheckovData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [expandedScanId, setExpandedScanId] = useState<string | null>(null);

//     // --- GraphQL Query (Unchanged) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             application { applicationName }
//             service { serviceName }
//             cluster { clusterName }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect (Unchanged) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedScanId(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData = response?.getCheckovResults || [];
//                 if (Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}
//                                     return timeB - timeA;
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                      }
//                 } else if (response && response.errors) { setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`); }
//                  else { setError("Could not fetch Checkov data or the format is incorrect."); }
//             } catch (err: any) { setError(err.message || "An unexpected error occurred."); }
//             finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // --- Event Handlers (Unchanged) ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) { setExpandedScanId(null); }
//     };
//     const handleScanSelect = (scanId: string | undefined | null) => {
//          if (!scanId) return;
//         setExpandedScanId(prevId => (prevId === scanId ? null : scanId));
//     };

//     // --- Helper Functions (Unchanged) ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//              if (typeof timestamp === 'number') { date = new Date(timestamp); }
//              else if (typeof timestamp === 'string') {
//                  const numericTimestamp = Number(timestamp);
//                  if (!isNaN(numericTimestamp) && timestamp.length > 5) { date = new Date(numericTimestamp); }
//                  else { date = new Date(timestamp); }
//              }
//              if (date && !isNaN(date.getTime())) { return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
//              else { return "Invalid Date"; }
//         } catch (e) { return "Invalid Date"; }
//     };
//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try {
//              if (!isNaN(dateObj.getTime())) { return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); }
//              else { return "Invalid Date"; }
//         } catch (e) { return "Invalid Date"; }
//     }
//     const getResourceDisplayName = (scan: any): string => {
//         const type = scan?.resourceType; const resourceId = scan?.resourceId ?? 'Unknown ID';
//         switch (type) {
//             case 'application': return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
//             case 'cluster': return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
//             case 'service': return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
//             default: return resourceId ?? 'Unknown Resource';
//         }
//     };
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//              if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                          if (!isNaN(nTs) && timestamp.length > 5) { scanDate = new Date(nTs); } else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) { if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; } }
//                  } catch(e) {}
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]);
//     // --- End Helper Functions ---


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//              <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                 </Box>
//              </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                              <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!Array.isArray(scans) || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             <List dense disablePadding>
//                                                 {scans.map((scan: any, index: number) => {
//                                                     const isExpanded = expandedScanId === scan?.id;
//                                                     return (
//                                                         <React.Fragment key={scan?.id ?? `scan-${index}`}>
//                                                             <ListItemButton
//                                                                 selected={isExpanded}
//                                                                 onClick={() => handleScanSelect(scan?.id)}
//                                                                 sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, borderBottom: !isExpanded && index < scans.length - 1 ? '1px solid #eee' : 'none' }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={getResourceDisplayName(scan)}
//                                                                     secondary={`Scanned: ${formatDate(scan?.updatedAt)}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
//                                                                      {/* --- CHANGE: Updated Chip Icons to match image --- */}
//                                                                      <Tooltip title={`Passed checks: ${scan?.summary?.passed ?? 'N/A'}`} placement="top">
//                                                                           <Chip
//                                                                               avatar={
//                                                                                  <Avatar sx={{ width: 18, height: 18, bgcolor: 'success.main' /* Green background */ }}>
//                                                                                     {/* Img with filter to make SVG symbol white */}
//                                                                                     <img src={PASSED_ICON_PATH} alt="Passed" width="12" height="12" style={{ filter: 'brightness(0) invert(1)' }} />
//                                                                                 </Avatar>
//                                                                               }
//                                                                               label={scan?.summary?.passed ?? 0}
//                                                                               size="small"
//                                                                               sx={{ bgcolor: '#e8f5e9', color: 'success.darker', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-avatar': { ml: '6px' }, '& .MuiChip-label': { pl: '4px', pr: '8px' } }} // Adjusted padding
//                                                                           />
//                                                                      </Tooltip>
//                                                                      <Tooltip title={`Failed checks: ${scan?.summary?.failed ?? 'N/A'}`} placement="top">
//                                                                           <Chip
//                                                                               avatar={
//                                                                                 <Avatar sx={{ width: 18, height: 18, bgcolor: 'error.main' /* Red background */ }}>
//                                                                                      {/* Img with filter to make SVG symbol white */}
//                                                                                     <img src={FAILED_ICON_PATH} alt="Failed" width="12" height="12" style={{ filter: 'brightness(0) invert(1)' }} />
//                                                                                 </Avatar>
//                                                                               }
//                                                                               label={scan?.summary?.failed ?? 0}
//                                                                               size="small"
//                                                                               sx={{ bgcolor: '#ffebee', color: 'error.darker', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-avatar': { ml: '6px' }, '& .MuiChip-label': { pl: '4px', pr: '8px' } }} // Adjusted padding
//                                                                           />
//                                                                     </Tooltip>
//                                                                     {/* --- END CHANGE --- */}
//                                                                 </Box>
//                                                             </ListItemButton>

//                                                             <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//                                                                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: '1px solid #eee' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {getResourceDisplayName(scan)} </Typography>
//                                                                     {/* Passed Checks Section */}
//                                                                     <Box mb={3}>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* --- CHANGE: Updated Detail Header Icon --- */}
//                                                                             <Avatar sx={{ width: 20, height: 20, bgcolor: 'success.main' }}>
//                                                                                  <img src={PASSED_ICON_PATH} alt="Passed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                             </Avatar>
//                                                                             {/* --- END CHANGE --- */}
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({scan?.passedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.passedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.passedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`pass-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.passedChecks.length - 1)}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                     {/* Failed Checks Section */}
//                                                                     <Box>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                              {/* --- CHANGE: Updated Detail Header Icon --- */}
//                                                                             <Avatar sx={{ width: 20, height: 20, bgcolor: 'error.main' }}>
//                                                                                 <img src={FAILED_ICON_PATH} alt="Failed" width="14" height="14" style={{ filter: 'brightness(0) invert(1)' }}/>
//                                                                             </Avatar>
//                                                                              {/* --- END CHANGE --- */}
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({scan?.failedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.failedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.failedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`fail-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                         {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse, // --- CHANGE: Import Collapse for animation ---
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- CHANGE: Removed MUI Check/Cancel Icons as we use custom SVGs ---
// // import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// // import CancelIcon from '@mui/icons-material/Cancel';
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Placeholder for Logos ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg";
// // --- CHANGE: Define paths for custom SVG icons ---
// const PASSED_ICON_PATH = "/assets/checkbox-circle-fill.svg"; // Replace with your actual path
// const FAILED_ICON_PATH = "/assets/close-circle-fill (1).svg"; // Replace with your actual path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     // --- CHANGE: State to track expanded scan *within* an accordion ---
//     const [expandedScanId, setExpandedScanId] = useState<string | null>(null);
//     // --- CHANGE: Removed selectedCheckovResult state as details are shown inline ---
//     // const [selectedCheckovResult, setSelectedCheckovResult] = useState<any | null>(null);

//     // --- GraphQL Query (Unchanged) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             application { applicationName }
//             service { serviceName }
//             cluster { clusterName }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect (Logic Unchanged) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             // Reset expanded scan ID on fetch
//             setExpandedScanId(null);
//             // setSelectedCheckovResult(null); // Removed
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData = response?.getCheckovResults || [];

//                 if (Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0;
//                                     let timeB = 0;
//                                     const tsA = a?.updatedAt;
//                                     const tsB = b?.updatedAt;
//                                     try {
//                                         const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA);
//                                         if (!isNaN(dateA.getTime())) { timeA = dateA.getTime(); }
//                                     } catch { /* ignore */ }
//                                     try {
//                                         const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB);
//                                          if (!isNaN(dateB.getTime())) { timeB = dateB.getTime(); }
//                                     } catch { /* ignore */ }
//                                     return timeB - timeA;
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                      }
//                 } else if (response && response.errors) {
//                      console.error("GraphQL Errors:", response.errors);
//                      setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 }
//                  else {
//                     console.error("Checkov data not found or is not an array:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                  }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- CHANGE: Removed effect to update selectedCheckovResult ---
//      /*
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item?.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);
//      */

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         // Collapse any open scan item when accordion changes
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//              setExpandedScanId(null);
//         }
//     };

//     // --- CHANGE: Handler to toggle inline scan details visibility ---
//     const handleScanSelect = (scanId: string | undefined | null) => {
//          if (!scanId) return; // Do nothing if scanId is invalid
//         setExpandedScanId(prevId => (prevId === scanId ? null : scanId)); // Toggle based on ID
//     };

//     // --- Helper Functions (Unchanged: formatDate, formatHeaderDate, getResourceDisplayName, useMemo for latest date) ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//              if (typeof timestamp === 'number') { date = new Date(timestamp); }
//              else if (typeof timestamp === 'string') {
//                  const numericTimestamp = Number(timestamp);
//                  if (!isNaN(numericTimestamp) && timestamp.length > 5) { date = new Date(numericTimestamp); }
//                  else { date = new Date(timestamp); }
//              }
//              if (date && !isNaN(date.getTime())) {
//                  return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//              } else { console.warn("Could not parse date:", timestamp); return "Invalid Date"; }
//         } catch (e) { console.error("Error formatting date:", e, timestamp); return "Invalid Date"; }
//     };
//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try {
//              if (!isNaN(dateObj.getTime())) { return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); }
//              else { return "Invalid Date"; }
//         } catch (e) { console.error("Error formatting header date:", e); return "Invalid Date"; }
//     }
//     const getResourceDisplayName = (scan: any): string => {
//         const type = scan?.resourceType;
//         const resourceId = scan?.resourceId ?? 'Unknown ID';
//         switch (type) {
//             case 'application': return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
//             case 'cluster': return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
//             case 'service': return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
//             default: return resourceId ?? 'Unknown Resource';
//         }
//     };
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//              if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const numericTimestamp = Number(timestamp);
//                          if (!isNaN(numericTimestamp) && timestamp.length > 5) { scanDate = new Date(numericTimestamp); }
//                          else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//                      } else { console.warn("Could not parse scan date for latest date calculation:", timestamp); }
//                  } catch(e) { console.error("Error processing scan date for latest date calculation:", timestamp, e); }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]);
//     // --- End Helper Functions ---


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//              <Box sx={{ mb: 3 }}>
//                  {/* ... Breadcrumbs and Title ... */}
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                 <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                 </Box>
//              </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                              <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!Array.isArray(scans) || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', /* borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', */ minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans - Rendered differently now */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan: any, index: number) => {
//                                                     const isExpanded = expandedScanId === scan?.id;
//                                                     return (
//                                                         // Use Fragment to group Button and Collapse
//                                                         <React.Fragment key={scan?.id ?? `scan-${index}`}>
//                                                             <ListItemButton
//                                                                 selected={isExpanded} // Highlight if expanded
//                                                                 onClick={() => handleScanSelect(scan?.id)}
//                                                                 sx={{
//                                                                     '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } },
//                                                                     px: { xs: 2, md: 3 }, py: 2,
//                                                                     borderBottom: !isExpanded && index < scans.length - 1 ? '1px solid #eee' : 'none' // Divider only if not expanded and not last
//                                                                 }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={getResourceDisplayName(scan)}
//                                                                     secondary={`Scanned: ${formatDate(scan?.updatedAt)}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
//                                                                     {/* --- CHANGE: Use custom SVG for Chips --- */}
//                                                                      <Tooltip title={`Passed checks: ${scan?.summary?.passed ?? 'N/A'}`} placement="top">
//                                                                           <Chip
//                                                                               // Use avatar prop with img tag for SVG
//                                                                               avatar={
//                                                                                  <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}>
//                                                                                     <img src={PASSED_ICON_PATH} alt="Passed" width="14" height="14" style={{ filter: 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)' /* Generated Green */ }} />
//                                                                                 </Avatar>
//                                                                               }
//                                                                               label={scan?.summary?.passed ?? 0}
//                                                                               size="small"
//                                                                               sx={{ bgcolor: '#e8f5e9', color: 'success.darker', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-avatar': { ml: '6px' }, '& .MuiChip-label': { px: '8px', mr: '2px' } }}
//                                                                           />
//                                                                      </Tooltip>
//                                                                      <Tooltip title={`Failed checks: ${scan?.summary?.failed ?? 'N/A'}`} placement="top">
//                                                                           <Chip
//                                                                               // Use avatar prop with img tag for SVG
//                                                                               avatar={
//                                                                                 <Avatar sx={{ width: 18, height: 18, bgcolor: 'transparent' }}>
//                                                                                     <img src={FAILED_ICON_PATH} alt="Failed" width="14" height="14" style={{ filter: 'invert(22%) sepia(98%) saturate(7499%) hue-rotate(358deg) brightness(96%) contrast(117%)' /* Generated Red */ }} />
//                                                                                 </Avatar>
//                                                                               }
//                                                                               label={scan?.summary?.failed ?? 0}
//                                                                               size="small"
//                                                                               sx={{ bgcolor: '#ffebee', color: 'error.darker', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-avatar': { ml: '6px' }, '& .MuiChip-label': { px: '8px', mr: '2px' } }}
//                                                                           />
//                                                                     </Tooltip>
//                                                                     {/* --- END CHANGE --- */}
//                                                                 </Box>
//                                                             </ListItemButton>

//                                                             {/* --- CHANGE: Nested Details Display --- */}
//                                                             <Collapse in={isExpanded} timeout="auto" unmountOnExit>
//                                                                  <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: '1px solid #eee' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}>
//                                                                         Details for Scan: {getResourceDisplayName(scan)} {/* Use scan directly */}
//                                                                     </Typography>
//                                                                     {/* Passed Checks Section (uses scan directly) */}
//                                                                     <Box mb={3}>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* Use custom SVG here too */}
//                                                                             <img src={PASSED_ICON_PATH} alt="Passed" width="20" height="20" style={{ filter: 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(86deg) brightness(118%) contrast(119%)' }}/>
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({scan?.passedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.passedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.passedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`pass-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.passedChecks.length - 1)}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                     {/* Failed Checks Section (uses scan directly) */}
//                                                                     <Box>
//                                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                                             {/* Use custom SVG here too */}
//                                                                              <img src={FAILED_ICON_PATH} alt="Failed" width="20" height="20" style={{ filter: 'invert(22%) sepia(98%) saturate(7499%) hue-rotate(358deg) brightness(96%) contrast(117%)' }}/>
//                                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({scan?.failedChecks?.length ?? 0}) </Typography>
//                                                                         </Box>
//                                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                                             {(scan?.failedChecks?.length ?? 0) > 0 ? (
//                                                                                 scan.failedChecks.map((check: any, checkIndex: number) => (
//                                                                                     <ListItem key={`fail-${check?.checkId ?? checkIndex}`} divider={checkIndex < (scan.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
//                                                                                         <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                                         {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                                     </ListItem>
//                                                                                 ))
//                                                                             ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                                         </List>
//                                                                     </Box>
//                                                                 </Box>
//                                                             </Collapse>
//                                                             {/* --- END Nested Details Display --- */}
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>

//                                             {/* --- CHANGE: Removed old details section and prompt --- */}
//                                             {/* {selectedCheckovResult && ... } */}
//                                             {/* {!selectedScanId && expandedAccordion === resourceType && ... } */}

//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     // Stack, // Not used
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- CHANGE START: Interfaces Removed ---
// // Reason: Per user request, interfaces are removed to handle data dynamically.
// // This increases flexibility for varying API responses but sacrifices type safety.
// // interface CheckDetail { ... } // Removed
// // interface CheckovResult { ... } // Removed
// // type GroupedCheckovData = { ... }; // Removed (dependent on CheckovResult)
// // --- CHANGE END ---


// // --- Placeholder for Logo ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Example path using svg


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     // --- CHANGE START: State types updated to use 'any' ---
//     // Reason: Reflects the removal of specific interfaces. Requires careful handling of data.
//     const [allCheckovData, setAllCheckovData] = useState<any[]>([]); // Use any[] for the array
//     const [groupedData, setGroupedData] = useState<any>({});      // Use any for the grouped object
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<any | null>(null); // Use any for the selected item
//     // --- CHANGE END ---

//     // --- GraphQL Query (Unchanged) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             # Requesting all fields needed, even without interfaces to enforce structure
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             application {
//               applicationName
//             }
//             service {
//               serviceName
//             }
//             cluster {
//               clusterName
//             }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 // --- CHANGE START: Assign fetched data directly to state typed as 'any[]' ---
//                 // No interface casting/validation needed here, but assumes response structure.
//                 const fetchedData = response?.getCheckovResults || [];
//                 // --- CHANGE END ---
//                 console.log(fetchedData);
//                 console.log("Output above.")
//                 // Basic check if it's an array before proceeding
//                 if (Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         // --- CHANGE START: Grouping logic now operates on 'any' type ---
//                         // Accessing properties like result.resourceType assumes they exist. Optional chaining recommended.
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType; // Use optional chaining for safety
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 acc[type]?.sort((a: any, b: any) => { // Explicitly type params as any in sort
//                                     // --- Robust date comparison logic (KEPT FROM PREVIOUS VERSION) ---
//                                     // Reason: Essential for handling potential string/number/invalid dates without type safety.
//                                     let timeA = 0;
//                                     let timeB = 0;
//                                     const tsA = a?.updatedAt; // Optional chaining
//                                     const tsB = b?.updatedAt; // Optional chaining
//                                     try {
//                                         const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA);
//                                         if (!isNaN(dateA.getTime())) { timeA = dateA.getTime(); }
//                                     } catch { /* ignore */ }
//                                     try {
//                                         const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB);
//                                          if (!isNaN(dateB.getTime())) { timeB = dateB.getTime(); }
//                                     } catch { /* ignore */ }
//                                     return timeB - timeA; // Descending
//                                     // --- End of Robust Date Comparison ---
//                                 });
//                             }
//                             return acc;
//                         }, {} as any); // Type accumulator as any
//                         setGroupedData(groups);
//                         // --- CHANGE END ---
//                      }
//                 } else if (response && response.errors) {
//                      console.error("GraphQL Errors:", response.errors);
//                      setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 }
//                  else {
//                     console.error("Checkov data not found or is not an array:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                  }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details ---
//      useEffect(() => {
//         if (selectedScanId) {
//             // Find still works, but the 'result' will be 'any'
//             const result = allCheckovData.find(item => item?.id === selectedScanId); // Use optional chaining on item.id
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers (Unchanged logic) ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//              setSelectedScanId(null);
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions ---

//     // --- Robust formatDate function (KEPT FROM PREVIOUS VERSION) ---
//     // Reason: Vital for handling string/number/null timestamps correctly without type guarantees.
//     const formatDate = (timestamp: string | number | null | undefined): string => { // Accept undefined too
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//              if (typeof timestamp === 'number') {
//                  date = new Date(timestamp);
//              } else if (typeof timestamp === 'string') {
//                  const numericTimestamp = Number(timestamp);
//                  if (!isNaN(numericTimestamp) && timestamp.length > 5) {
//                      date = new Date(numericTimestamp);
//                  } else {
//                      date = new Date(timestamp);
//                  }
//              }
//              if (date && !isNaN(date.getTime())) {
//                  return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//              } else {
//                  console.warn("Could not parse date:", timestamp);
//                  return "Invalid Date";
//              }
//         } catch (e) {
//             console.error("Error formatting date:", e, timestamp);
//             return "Invalid Date";
//         }
//     };
//     // --- End of formatDate ---

//     // --- Robust formatHeaderDate function (KEPT FROM PREVIOUS VERSION) ---
//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try {
//              if (!isNaN(dateObj.getTime())) {
//                  return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//              } else {
//                  return "Invalid Date";
//              }
//         } catch (e) {
//             console.error("Error formatting header date:", e);
//             return "Invalid Date";
//         }
//     }
//     // --- End of formatHeaderDate ---

//     // --- CHANGE START: Update function signature to accept 'any' ---
//     const getResourceDisplayName = (scan: any): string => {
//         // Reason: Reflects that 'scan' no longer has a specific interface type.
//         // Optional chaining '?' and nullish coalescing '??' are highly recommended here for safety.
//         const type = scan?.resourceType;
//         const resourceId = scan?.resourceId ?? 'Unknown ID'; // Provide default
//         switch (type) {
//             case 'application':
//                 return scan?.application?.applicationName ?? resourceId ?? 'Unknown Application';
//             case 'cluster':
//                 return scan?.cluster?.clusterName ?? resourceId ?? 'Unknown Cluster';
//             case 'service':
//                 return scan?.service?.serviceName ?? resourceId ?? 'Unknown Service';
//             default:
//                 return resourceId ?? 'Unknown Resource'; // Use the fetched resourceId or fallback
//         }
//     };
//     // --- CHANGE END ---

//     // --- Calculate LATEST SCAN DATE for Header Card ---
//     // --- Robust date calculation logic (KEPT FROM PREVIOUS VERSION) ---
//     // Reason: Essential for handling potentially mixed/invalid date formats safely.
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const timestamp = scan?.updatedAt; // Use optional chaining
//              if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') {
//                          scanDate = new Date(timestamp);
//                      } else if (typeof timestamp === 'string') {
//                          const numericTimestamp = Number(timestamp);
//                          if (!isNaN(numericTimestamp) && timestamp.length > 5) {
//                             scanDate = new Date(numericTimestamp);
//                          } else {
//                             scanDate = new Date(timestamp);
//                          }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) {
//                              latestDateObj = scanDate;
//                          }
//                      } else {
//                          console.warn("Could not parse scan date for latest date calculation:", timestamp);
//                      }
//                  } catch(e) {
//                     console.error("Error processing scan date for latest date calculation:", timestamp, e);
//                  }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]);
//     // --- End of Robust Date Calculation ---


//     // --- Render Logic (Largely Unchanged UI, but accesses properties on 'any' types) ---
//     // IMPORTANT: Accessing properties like scan.id, scan.summary.passed, check.checkId etc.,
//     // now relies on the actual data structure at runtime. Use optional chaining (?.)
//     // and nullish coalescing (??) frequently if unsure about data presence.
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//              <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                 <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                 <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                 </Box>
//             </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             {/* ... (Header card content remains the same, uses formatHeaderDate) ... */}
//                              <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto">
//                                     <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> C </Avatar>
//                                 </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography>
//                                 </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                              {/* Iterate over known keys, data itself is 'any' */}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 // Access grouped data using the key, result is any[] or undefined
//                                 const scans = groupedData[resourceType];
//                                 // Check if scans exist and is an array with length
//                                 if (!Array.isArray(scans) || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} /* ... Accordion props ... */ sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }}>
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} /* ... Summary props ... */ sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             {/* Use scans.length safely as we checked Array.isArray above */}
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan: any, index: number) => ( // Explicitly type scan as any here
//                                                     <ListItemButton key={scan?.id ?? `scan-${index}`} selected={selectedScanId === scan?.id} onClick={() => handleScanSelect(scan?.id)} divider={index < scans.length - 1} sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, }} >
//                                                         <ListItemText
//                                                             primary={getResourceDisplayName(scan)} // Uses helper function
//                                                             secondary={`Scanned: ${formatDate(scan?.updatedAt)}`} // Uses helper function + optional chaining
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: selectedScanId === scan?.id ? 'primary.main' : 'text.primary' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                         />
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
//                                                              {/* Access summary fields with optional chaining and nullish coalescing */}
//                                                              <Tooltip title={`Passed checks: ${scan?.summary?.passed ?? 'N/A'}`} placement="top">
//                                                                   <Chip icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#fff', ml: '6px' }} />} label={scan?.summary?.passed ?? 0} size="small" sx={{ bgcolor: '#4CAF50', color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-label': { px: 1 } }} />
//                                                              </Tooltip>
//                                                              <Tooltip title={`Failed checks: ${scan?.summary?.failed ?? 'N/A'}`} placement="top">
//                                                                   <Chip icon={<CancelIcon sx={{ fontSize: 18, color: '#fff', ml: '6px' }} />} label={scan?.summary?.failed ?? 0} size="small" sx={{ bgcolor: '#F44336', color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-label': { px: 1 } }} />
//                                                             </Tooltip>
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display */}
//                                             {/* Use optional chaining extensively when accessing selectedCheckovResult properties */}
//                                             {selectedCheckovResult && selectedCheckovResult?.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {getResourceDisplayName(selectedCheckovResult)} </Typography>
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                          {/* Safely access passedChecks length */}
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult?.passedChecks?.length ?? 0}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {(selectedCheckovResult?.passedChecks?.length ?? 0) > 0 ? (
//                                                                  selectedCheckovResult.passedChecks.map((check: any, index: number) => ( // Type check as any
//                                                                      <ListItem key={`pass-${check?.checkId ?? index}`} divider={index < (selectedCheckovResult.passedChecks.length - 1)}>
//                                                                          <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                      </ListItem>
//                                                                  ))
//                                                              ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                          {/* Safely access failedChecks length */}
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult?.failedChecks?.length ?? 0}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                              {(selectedCheckovResult?.failedChecks?.length ?? 0) > 0 ? (
//                                                                  selectedCheckovResult.failedChecks.map((check: any, index: number) => ( // Type check as any
//                                                                      <ListItem key={`fail-${check?.checkId ?? index}`} divider={index < (selectedCheckovResult.failedChecks.length - 1)} sx={{ alignItems: 'flex-start'}}>
//                                                                          <ListItemText primary={check?.checkId ?? 'Unknown ID'} secondary={check?.checkName || check?.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} />
//                                                                          {/* Safely access severity */}
//                                                                          {check?.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                      </ListItem>
//                                                                  ))
//                                                              ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* Prompt */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     // ListItemIcon, // Removed as it wasn't used in the final accordion summary
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- Icons for Pass/Fail Indicators ---
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// // --- Other Icons (Placeholder, not used in final accordion summary) ---
// // import DescriptionIcon from '@mui/icons-material/Description';
// // import AppsIcon from '@mui/icons-material/Apps';
// // import StorageIcon from '@mui/icons-material/Storage';
// // import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Interfaces (Structure of your data) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }

// // *** UPDATED CheckovResult Interface ***
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
//     // Added application, cluster, service details
//     application: { applicationName: string } | null;
//     cluster: { clusterName: string } | null;
//     service: { serviceName: string } | null;
// }

// // --- Type for organizing data by resource type ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Example path using svg


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query ---
//     // *** UPDATED GraphQL Query String ***
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description checkClass resourceAddress shortDescription vulnerabilityDetails }
//             failedChecks { checkId checkName resource severity description resourceAddress checkClass shortDescription vulnerabilityDetails }
//             summary { checkovVersion failed passed parsingErrors resourceCount skipped }
//             createdAt
//             updatedAt
//             # Added fields to get resource names
//             application {
//               applicationName
//             }
//             service {
//               serviceName
//             }
//             cluster {
//               clusterName
//             }
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 // Added optional chaining for safety
//                 const fetchedData: CheckovResult[] | undefined = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             // Ensure result and type are valid before processing
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort within the group after adding
//                                 acc[type]?.sort((a, b) => {
//                                     // Handle potential null/invalid dates gracefully
//                                     const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
//                                     const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
//                                     return dateB - dateA; // Descending order
//                                 });
//                             }
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else if (response && response.errors) { // Handle GraphQL errors
//                      console.error("GraphQL Errors:", response.errors);
//                      setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 }
//                  else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Removed getCheckovResultsQuery from dependencies as it's constant

//      // --- Effect to Update Selected Scan Details ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         // Deselect scan if accordion is collapsed or a different one is opened
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//              // setSelectedCheckovResult(null); // Also clear the selected result data
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         // Check if it's a potentially numeric string representing milliseconds
//         const timestamp = Number(isoString);
//         if (!isNaN(timestamp) && isoString.length > 5) { // Basic check for numeric timestamp
//              try { return new Date(timestamp).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { /* fallback */ }
//         }
//         // Assume ISO string format otherwise
//         try { return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { console.error("Error formatting date:", e, isoString); return "Invalid Date"; }
//     };

//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//          try { return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch (e) { console.error("Error formatting header date:", e); return "Invalid Date"; }
//     }

//     // *** ADDED Helper to get display name ***
//     const getResourceDisplayName = (scan: CheckovResult): string => {
//         switch (scan.resourceType) {
//             case 'application':
//                 return scan.application?.applicationName || scan.resourceId || 'Unknown Application'; // Fallback chain
//             case 'cluster':
//                 return scan.cluster?.clusterName || scan.resourceId || 'Unknown Cluster';       // Fallback chain
//             case 'service':
//                 return scan.service?.serviceName || scan.resourceId || 'Unknown Service';       // Fallback chain
//             default:
//                 return scan.resourceId || 'Unknown Resource'; // Default fallback
//         }
//     };
//     /* Removed as icons are not used in the final accordion summary
//      const getResourceTypeIcon = (type: string) => {
//          switch (type) { case 'application': return <AppsIcon />; case 'cluster': return <StorageIcon />; case 'service': return <SettingsEthernetIcon />; default: return <DescriptionIcon />; }
//      };
//     */

//     // --- Calculate LATEST SCAN DATE for Header Card ---
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//              if (scan?.updatedAt) {
//                  try {
//                     const timestamp = Number(scan.updatedAt);
//                     const scanDate = !isNaN(timestamp) && scan.updatedAt.length > 5 ? new Date(timestamp) : new Date(scan.updatedAt);
//                     if (!isNaN(scanDate.getTime())) { // Check if date is valid
//                          if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//                     } else {
//                         console.warn("Could not parse scan date:", scan.updatedAt);
//                     }
//                  } catch(e) { console.error("Error processing scan date:", scan.updatedAt, e); }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* --- 1. Page Header --- */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* --- 3. Content Display --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}

//                         {/* ========== HEADER CARD ========== */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             <Grid container spacing={2} alignItems="center">
//                                 {/* --- Logo Column --- */}
//                                 <Grid item xs={12} sm="auto">
//                                     <Avatar
//                                         src={CHECKOV_LOGO_PATH}
//                                         alt="Checkov Logo"
//                                         sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }}
//                                         variant="circular"
//                                      > C </Avatar> {/* Fallback initial */}
//                                 </Grid>
//                                 {/* --- Text Content Column --- */}
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}>
//                                         Infrastructure as Code (IaC) static analysis tool for security and compliance.
//                                     </Typography>
//                                 </Grid>
//                                 {/* --- Date Column --- */}
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography>
//                                 </Grid>
//                             </Grid>
//                         </Paper>
//                          {/* ========== END HEADER CARD ========== */}


//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null; // Skip rendering if no scans for this type
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }} >
//                                             {/* Icon was removed */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     <ListItemButton key={scan.id} selected={selectedScanId === scan.id} onClick={() => handleScanSelect(scan.id)} divider={index < scans.length - 1} sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, }} >
//                                                         {/* *** MODIFIED ListItemText Primary Prop *** */}
//                                                         <ListItemText
//                                                             primary={getResourceDisplayName(scan)} // Use the name here
//                                                             secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: selectedScanId === scan.id ? 'primary.main' : 'text.primary' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                         />
//                                                         {/* Scan List Chips */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
//                                                              <Tooltip title={`Passed checks: ${scan.summary.passed}`} placement="top">
//                                                                   <Chip icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#fff', ml: '6px' }} />} label={scan.summary.passed} size="small" sx={{ bgcolor: '#4CAF50', color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-label': { px: 1 } }} />
//                                                              </Tooltip>
//                                                              <Tooltip title={`Failed checks: ${scan.summary.failed}`} placement="top">
//                                                                   <Chip icon={<CancelIcon sx={{ fontSize: 18, color: '#fff', ml: '6px' }} />} label={scan.summary.failed} size="small" sx={{ bgcolor: '#F44336', color: '#fff', height: '28px', borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { }, '& .MuiChip-label': { px: 1 } }} />
//                                                             </Tooltip>
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {getResourceDisplayName(selectedCheckovResult)} </Typography> {/* Also use name here */}
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* Prompt */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar, // Using Avatar for the logo
//     Stack,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- Icons for Pass/Fail Indicators ---
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// // --- Other Icons ---
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// import queryGraphql from "../../helpers/ApiInterface";

// // Assuming helpers exist and work correctly
// // Replace with your actual import and type if available

// // --- Interfaces (Structure of your data) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for organizing data by resource type ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// // *** IMPORTANT: Update this path to the actual location of your Checkov logo image ***
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Example path using svg


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] | undefined = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) { acc[type] = []; }
//                             if(result) { acc[type]!.push(result); }
//                             acc[type]?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try { return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { console.error("Error formatting date:", e); return "Invalid Date"; }
//     };
//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//          try { return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch (e) { console.error("Error formatting header date:", e); return "Invalid Date"; }
//     }
//      const getResourceTypeIcon = (type: string) => {
//          switch (type) { case 'application': return <AppsIcon />; case 'cluster': return <StorageIcon />; case 'service': return <SettingsEthernetIcon />; default: return <DescriptionIcon />; }
//      };

//     // --- Calculate LATEST SCAN DATE for Header Card ---
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//              if (scan?.updatedAt) {
//                  try {
//                     const scanDate = new Date(scan.updatedAt);
//                     if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//                  } catch(e) { console.error("Could not parse scan date:", scan.updatedAt, e); }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj ? latestDateObj : null };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* --- 1. Page Header --- */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* --- 3. Content Display --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}

//                         {/* ========== HEADER CARD ========== */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             <Grid container spacing={2} alignItems="center">
//                                 {/* --- Logo Column: Changed variant to 'circular' --- */}
//                                 <Grid item xs={12} sm="auto">
//                                     <Avatar
//                                         src={CHECKOV_LOGO_PATH}
//                                         alt="Checkov Logo"
//                                         sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }}
//                                         variant="circular" // <-- CHANGED HERE
//                                      > C </Avatar>
//                                 </Grid>
//                                 {/* --- Text Content Column --- */}
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}>
//                                         Infrastructure as Code (IaC) static analysis tool for security and compliance.
//                                     </Typography>
//                                 </Grid>
//                                 {/* --- Date Column --- */}
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography>
//                                 </Grid>
//                             </Grid>
//                         </Paper>
//                          {/* ========== END HEADER CARD ========== */}


//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }} >
//                                             {/* <ListItemIcon>{getResourceTypeIcon(resourceType)}</ListItemIcon> */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     <ListItemButton key={scan.id} selected={selectedScanId === scan.id} onClick={() => handleScanSelect(scan.id)} divider={index < scans.length - 1} sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, }} >
//                                                         <ListItemText
//                                                             primary={scan.resourceId}
//                                                             secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }}
//                                                         />
//                                                         {/* Scan List Chips */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
//                                                             <Chip icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#fff' }} />} label={`Passed Checks: ${scan.summary.passed}`} size="small" title="Passed checks in this scan" sx={{ bgcolor: '#4CAF50', color: '#fff', height: '28px', px: 1.5, borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { ml: 0.5 }, '& .MuiChip-label': { pl: 0.5 } }} />
//                                                             <Chip icon={<CancelIcon sx={{ fontSize: 18, color: '#fff' }} />} label={`Failed Checks: ${scan.summary.failed}`} size="small" title="Failed checks in this scan" sx={{ bgcolor: '#F44336', color: '#fff', height: '28px', px: 1.5, borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { ml: 0.5 }, '& .MuiChip-label': { pl: 0.5 } }} />
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {selectedCheckovResult.resourceId} </Typography>
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* Prompt */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack, // Keep Stack import
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- Icons for Pass/Fail Indicators ---
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// // --- Other Icons ---
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// import queryGraphql from "../../helpers/ApiInterface";


// // --- Interfaces (Structure of your data) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for organizing data by resource type ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// // *** IMPORTANT: Update this path to the actual location of your Checkov logo image ***
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Example path using svg


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] | undefined = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) { acc[type] = []; }
//                             if(result) { acc[type]!.push(result); }
//                             acc[type]?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try { return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { console.error("Error formatting date:", e); return "Invalid Date"; }
//     };
//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//          try { return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch (e) { console.error("Error formatting header date:", e); return "Invalid Date"; }
//     }
//      const getResourceTypeIcon = (type: string) => {
//          switch (type) { case 'application': return <AppsIcon />; case 'cluster': return <StorageIcon />; case 'service': return <SettingsEthernetIcon />; default: return <DescriptionIcon />; }
//      };

//     // --- Calculate LATEST SCAN DATE for Header Card ---
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//              if (scan?.updatedAt) {
//                  try {
//                     const scanDate = new Date(scan.updatedAt);
//                     if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//                  } catch(e) { console.error("Could not parse scan date:", scan.updatedAt, e); }
//             }
//         });
//         // Return date as ISO string or null
//         return { overallLatestScanDate: latestDateObj ? latestDateObj : null };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* --- 1. Page Header --- */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* --- 3. Content Display --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}

//                         {/* ========== HEADER CARD: ADJUSTED SIZE AND ALIGNMENT ========== */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         {/* --- Reduced Padding, Consistent Border Radius --- */}
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             {/* --- Grid container ensures vertical alignment --- */}
//                             <Grid container spacing={2} alignItems="center">
//                                 {/* --- Logo Column: Reduced Size --- */}
//                                 <Grid item xs={12} sm="auto">
//                                     <Avatar
//                                         src={CHECKOV_LOGO_PATH}
//                                         alt="Checkov Logo"
//                                         sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} // Reduced size
//                                         variant="rounded"
//                                      > C </Avatar>
//                                 </Grid>
//                                 {/* --- Text Content Column --- */}
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     {/* --- Reduced margin-bottom on description --- */}
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 /* Removed extra margin/placeholder */ }}>
//                                         Infrastructure as Code (IaC) static analysis tool for security and compliance.
//                                     </Typography>
//                                     {/* --- Placeholder Box Removed --- */}
//                                 </Grid>
//                                 {/* --- Date Column: Ensures alignment --- */}
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> {/* Adjusted top margin for small screens */}
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography>
//                                 </Grid>
//                             </Grid>
//                         </Paper>
//                          {/* ========== END HEADER CARD ADJUSTMENTS ========== */}


//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     // Accordion structure remains the same
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }} >
//                                             {/* <ListItemIcon>{getResourceTypeIcon(resourceType)}</ListItemIcon> */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     <ListItemButton key={scan.id} selected={selectedScanId === scan.id} onClick={() => handleScanSelect(scan.id)} divider={index < scans.length - 1} sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 2, }} >
//                                                         <ListItemText
//                                                             primary={scan.resourceId}
//                                                             secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }}
//                                                         />
//                                                         {/* Scan List Chips */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
//                                                             <Chip icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#fff' }} />} label={`Passed Checks: ${scan.summary.passed}`} size="small" title="Passed checks in this scan" sx={{ bgcolor: '#4CAF50', color: '#fff', height: '28px', px: 1.5, borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { ml: 0.5 }, '& .MuiChip-label': { pl: 0.5 } }} />
//                                                             <Chip icon={<CancelIcon sx={{ fontSize: 18, color: '#fff' }} />} label={`Failed Checks: ${scan.summary.failed}`} size="small" title="Failed checks in this scan" sx={{ bgcolor: '#F44336', color: '#fff', height: '28px', px: 1.5, borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { ml: 0.5 }, '& .MuiChip-label': { pl: 0.5 } }} />
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {selectedCheckovResult.resourceId} </Typography>
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* Prompt */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- Icons for Pass/Fail Indicators ---
// import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Used for Scan List Passed Chip & Detail View Header
// import CancelIcon from '@mui/icons-material/Cancel'; // Used for Scan List Failed Chip & Detail View Header
// // --- Other Icons ---
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// import queryGraphql from "../../helpers/ApiInterface";
// // Assuming helpers exist and work correctly
// // Replace with your actual import and type if available


// // --- Interfaces (Structure of your data) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for organizing data by resource type ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// // *** IMPORTANT: Update this path to the actual location of your Checkov logo image ***
// const CHECKOV_LOGO_PATH = "/assets/checkov.svg"; // Example path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] | undefined = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) { acc[type] = []; }
//                             if(result) { acc[type]!.push(result); }
//                             acc[type]?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try { return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { console.error("Error formatting date:", e); return "Invalid Date"; }
//     };
//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//          try { return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch (e) { console.error("Error formatting header date:", e); return "Invalid Date"; }
//     }
//      const getResourceTypeIcon = (type: string) => {
//          switch (type) { case 'application': return <AppsIcon />; case 'cluster': return <StorageIcon />; case 'service': return <SettingsEthernetIcon />; default: return <DescriptionIcon />; }
//      };

//     // --- Calculate LATEST SCAN DATE for Header Card ---
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//              if (scan?.updatedAt) {
//                  try {
//                     const scanDate = new Date(scan.updatedAt);
//                     if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//                  } catch(e) { console.error("Could not parse scan date:", scan.updatedAt, e); }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj ? latestDateObj : null };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         // Adjusted Container Padding
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* --- 1. Page Header --- */}
//              {/* Adjusted Header Margin */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* --- 3. Content Display --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         {/* Adjusted Header Card Padding and Margin */}
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, overflow: 'hidden' }}>
//                             <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 60, height: 60, bgcolor: '#eeeeee' }} variant="rounded" > C </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 1.5 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                     <Box sx={{ minHeight: '26px' }} /> {/* Placeholder */}
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     // Adjusted Accordion Margin Bottom
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         {/* Adjusted Accordion Summary Padding */}
//                                         <AccordionSummary
//                                             expandIcon={<ExpandMoreIcon />}
//                                             aria-controls={`${resourceType}-content`}
//                                             id={`${resourceType}-header`}
//                                             sx={{
//                                                 bgcolor: 'background.paper',
//                                                 borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none',
//                                                 minHeight: '56px', // Keep minHeight
//                                                 px: { xs: 2, md: 3 }, // Consistent horizontal padding
//                                                 py: 1, // Adjust vertical padding if needed
//                                                 '& .MuiAccordionSummary-content': { alignItems: 'center' }
//                                             }}
//                                         >
//                                             {/* <ListItemIcon>{getResourceTypeIcon(resourceType)}</ListItemIcon> */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* === List of Scans === */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     // Adjusted Scan List Item Padding
//                                                     <ListItemButton
//                                                         key={scan.id}
//                                                         selected={selectedScanId === scan.id}
//                                                         onClick={() => handleScanSelect(scan.id)}
//                                                         divider={index < scans.length - 1}
//                                                         sx={{
//                                                             '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } },
//                                                             px: { xs: 2, md: 3 }, // Keep consistent horizontal padding
//                                                             py: 2, // Increased vertical padding
//                                                          }}
//                                                     >
//                                                         <ListItemText
//                                                             primary={scan.resourceId}
//                                                             secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }}
//                                                         />
//                                                         {/* Adjusted Spacing Before Chips */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 /* Increased margin-left */ }}>
//                                                             {/* Passed Chip */}
//                                                             <Chip
//                                                                 icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#fff' }} />}
//                                                                 label={`Passed Checks: ${scan.summary.passed}`}
//                                                                 size="small"
//                                                                 title="Passed checks in this scan"
//                                                                 sx={{ bgcolor: '#4CAF50', color: '#fff', height: '28px', px: 1.5, borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { ml: 0.5 }, '& .MuiChip-label': { pl: 0.5 } }}
//                                                             />
//                                                             {/* Failed Chip */}
//                                                             <Chip
//                                                                 icon={<CancelIcon sx={{ fontSize: 18, color: '#fff' }} />}
//                                                                 label={`Failed Checks: ${scan.summary.failed}`}
//                                                                 size="small"
//                                                                 title="Failed checks in this scan"
//                                                                 sx={{ bgcolor: '#F44336', color: '#fff', height: '28px', px: 1.5, borderRadius: '16px', fontSize: '0.8rem', fontWeight: 500, '& .MuiChip-icon': { ml: 0.5 }, '& .MuiChip-label': { pl: 0.5 } }}
//                                                             />
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 // Adjusted Detail View Padding
//                                                 <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {selectedCheckovResult.resourceId} </Typography>
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* Prompt */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack, // Keep Stack import, might be useful elsewhere
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- Icons for Pass/Fail Indicators ---
// import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Used for Scan List Passed Chip & Detail View Header
// import CancelIcon from '@mui/icons-material/Cancel'; // Used for Scan List Failed Chip & Detail View Header
// // --- Other Icons ---
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// import queryGraphql from "../../helpers/ApiInterface";


// // --- Interfaces (Structure of your data) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for organizing data by resource type ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// // *** IMPORTANT: Update this path to the actual location of your Checkov logo image ***
// const CHECKOV_LOGO_PATH = "/images/checkov-logo.png"; // Example path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] | undefined = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) { acc[type] = []; }
//                             if(result) { acc[type]!.push(result); }
//                             acc[type]?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try { return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { console.error("Error formatting date:", e); return "Invalid Date"; }
//     };
//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//          try { return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch (e) { console.error("Error formatting header date:", e); return "Invalid Date"; }
//     }
//      const getResourceTypeIcon = (type: string) => {
//          switch (type) { case 'application': return <AppsIcon />; case 'cluster': return <StorageIcon />; case 'service': return <SettingsEthernetIcon />; default: return <DescriptionIcon />; }
//      };

//     // --- Calculate LATEST SCAN DATE for Header Card (Totals not needed) ---
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//              if (scan?.updatedAt) {
//                  try {
//                     const scanDate = new Date(scan.updatedAt);
//                     if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//                  } catch(e) { console.error("Could not parse scan date:", scan.updatedAt, e); }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj ? latestDateObj : null };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: "16px", md: "24px" }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* --- 1. Page Header --- */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center">
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* --- 3. Content Display --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}
//                         {/* Header Card (No aggregated Pass/Fail chips) */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: { xs: 2, md: 3 }, mb: 4, overflow: 'hidden' }}>
//                             <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 60, height: 60, bgcolor: '#eeeeee' }} variant="rounded" > C </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 1.5 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                     <Box sx={{ minHeight: '26px' }} /> {/* Placeholder */}
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', '& .MuiAccordionSummary-content': { alignItems: 'center' } }} >
//                                             {/* <ListItemIcon>{getResourceTypeIcon(resourceType)}</ListItemIcon> */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* === List of Scans === */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     <ListItemButton key={scan.id} selected={selectedScanId === scan.id} onClick={() => handleScanSelect(scan.id)} divider={index < scans.length - 1} sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 1.5, }} >
//                                                         <ListItemText
//                                                             primary={scan.resourceId}
//                                                             secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }}
//                                                         />
//                                                         {/* --- MODIFIED CHIPS (Style like image_35eddd.png) --- */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 1, flexShrink: 0 /* Prevent shrinking */ }}>
//                                                             {/* Passed Chip */}
//                                                             <Chip
//                                                                 icon={<CheckCircleIcon sx={{ fontSize: 18, color: '#fff' }} />} // Filled Check, white icon
//                                                                 label={`Passed Checks: ${scan.summary.passed}`} // Text label + count
//                                                                 size="small"
//                                                                 title="Passed checks in this scan"
//                                                                 sx={{
//                                                                     bgcolor: '#4CAF50', // Solid green (use theme success.main if preferred)
//                                                                     color: '#fff',
//                                                                     height: '28px', // Match image height
//                                                                     px: 1.5, // Padding left/right
//                                                                     borderRadius: '16px', // Pill shape
//                                                                     fontSize: '0.8rem', // Adjust font size if needed
//                                                                     fontWeight: 500,
//                                                                     '& .MuiChip-icon': { ml: 0.5 }, // Space before icon
//                                                                     '& .MuiChip-label': { pl: 0.5 } // Space after icon
//                                                                 }}
//                                                             />
//                                                             {/* Failed Chip */}
//                                                             <Chip
//                                                                 icon={<CancelIcon sx={{ fontSize: 18, color: '#fff' }} />} // Cancel icon (cross), white
//                                                                 label={`Failed Checks: ${scan.summary.failed}`} // Text label + count
//                                                                 size="small"
//                                                                 title="Failed checks in this scan"
//                                                                 sx={{
//                                                                     bgcolor: '#F44336', // Solid red (use theme error.main if preferred)
//                                                                     color: '#fff',
//                                                                     height: '28px',
//                                                                     px: 1.5,
//                                                                     borderRadius: '16px',
//                                                                     fontSize: '0.8rem',
//                                                                     fontWeight: 500,
//                                                                     '& .MuiChip-icon': { ml: 0.5 },
//                                                                     '& .MuiChip-label': { pl: 0.5 }
//                                                                 }}
//                                                             />
//                                                         </Box>
//                                                         {/* --- END MODIFIED CHIPS --- */}
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display (unchanged logic) */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {selectedCheckovResult.resourceId} </Typography>
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* Prompt */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // --- Icons for Pass/Fail Indicators ---
// import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Use filled icon for Passed
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Keep outline icon for Failed
// // --- Icons for other parts ---
// import CancelIcon from '@mui/icons-material/Cancel'; // Used in detail view header
// // import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // No longer needed for scan list chips
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';


// // Assuming helpers exist
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Interfaces (unchanged) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for grouped data (unchanged) ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo (unchanged) ---
// const CHECKOV_LOGO_PATH = "/images/checkov-logo.png"; // *** UPDATE THIS PATH ***


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables (unchanged) ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query (unchanged) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect (unchanged) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;
//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]); setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) { acc[type] = []; }
//                             acc[type]!.push(result);
//                             acc[type]!.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally { setIsLoading(false); }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details (unchanged) ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else { setSelectedCheckovResult(null); }
//      }, [selectedScanId, allCheckovData]);

//     // --- Event Handlers (unchanged) ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//              setSelectedCheckovResult(null);
//         }
//     };
//     const handleScanSelect = (scanId: string) => { setSelectedScanId(scanId); };

//     // --- Helper Functions (unchanged) ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A"; try { return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return "Invalid Date"; }
//     };
//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A"; try { return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }); } catch (e) { return "Invalid Date"; }
//     }
//     const getResourceTypeIcon = (type: string) => {
//          switch (type) { case 'application': return <AppsIcon />; case 'cluster': return <StorageIcon />; case 'service': return <SettingsEthernetIcon />; default: return <DescriptionIcon />; }
//      };

//     // --- Calculate LATEST SCAN DATE for Header Card (unchanged) ---
//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allCheckovData.forEach(scan => {
//             const scanDate = new Date(scan.updatedAt);
//             if (!latestDateObj || scanDate > latestDateObj) { latestDateObj = scanDate; }
//         });
//         return { overallLatestScanDate: latestDateObj ? latestDateObj : null, };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: "16px", md: "24px" }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* --- 1. Page Header (unchanged) --- */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center">
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area (unchanged wrapper) --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States (unchanged) */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}
//                 {!isLoading && !error && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. Run a scan to see results here. </Typography> </Paper> )}

//                 {/* --- 3. Content Display (if data loaded successfully) --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}
//                         {/* Header Card (unchanged) */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of checks executed by Checkov. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: { xs: 2, md: 3 }, mb: 4, overflow: 'hidden' }}>
//                             <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 60, height: 60, bgcolor: '#eeeeee' }} variant="rounded" > C </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 1.5 }}> Infrastructure as Code (IaC) static analysis tool for security and compliance. </Typography>
//                                     <Box sx={{ minHeight: '26px' }} />
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Scanned On: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types */}
//                         <Box>
//                              {Object.keys(groupedData).length === 0 && !isLoading && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No scan results found for any resource type after loading. </Typography> )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', '& .MuiAccordionSummary-content': { alignItems: 'center' } }} >
//                                             {/* <ListItemIcon>...</ListItemIcon> */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* === List of Scans (MODIFIED CHIPS) === */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     <ListItemButton key={scan.id} selected={selectedScanId === scan.id} onClick={() => handleScanSelect(scan.id)} divider={index < scans.length - 1} sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 1.5, }} >
//                                                         <ListItemText primary={scan.resourceId} secondary={`Scanned: ${formatDate(scan.updatedAt)}`} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }} />
//                                                         {/* --- MODIFIED CHIPS --- */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
//                                                             {/* Passed Chip */}
//                                                             <Chip
//                                                                 // Use filled CheckCircleIcon
//                                                                 icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#fff', ml: '6px' /* Adjust icon spacing */ }} />}
//                                                                 label={scan.summary.passed}
//                                                                 size="small"
//                                                                 sx={{
//                                                                     bgcolor: 'success.main', // Solid green background
//                                                                     color: '#fff', // White text
//                                                                     height: '24px', // Slightly taller
//                                                                     borderRadius: '12px', // Pill shape
//                                                                     fontSize: '0.8rem',
//                                                                     fontWeight: 500,
//                                                                     '& .MuiChip-label': { px: '8px' }, // Adjust label padding
//                                                                     '& .MuiChip-icon': { mr: '-4px' } // Fine-tune icon margin if needed
//                                                                 }}
//                                                             />
//                                                             {/* Failed Chip */}
//                                                             <Chip
//                                                                 // Keep ErrorOutlineIcon
//                                                                 icon={<ErrorOutlineIcon sx={{ fontSize: 16, color: '#fff', ml: '6px' }} />}
//                                                                 label={scan.summary.failed}
//                                                                 size="small"
//                                                                 sx={{
//                                                                     bgcolor: 'error.main', // Solid red background
//                                                                     color: '#fff', // White text
//                                                                     height: '24px',
//                                                                     borderRadius: '12px',
//                                                                     fontSize: '0.8rem',
//                                                                     fontWeight: 500,
//                                                                     '& .MuiChip-label': { px: '8px' },
//                                                                     '& .MuiChip-icon': { mr: '-4px' }
//                                                                 }}
//                                                             />
//                                                         </Box>
//                                                         {/* --- END MODIFIED CHIPS --- */}
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display (unchanged logic) */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && ( <Box sx={{ p: { xs: 2, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}> <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {selectedCheckovResult.resourceId} </Typography> {/* Passed Checks Section */} <Box mb={3}> <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box> <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}> {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )} </List> </Box> {/* Failed Checks Section */} <Box> <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box> <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}> {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )} </List> </Box> </Box> )}

//                                             {/* Prompt (unchanged) */}
//                                             {!selectedScanId && expandedAccordion === resourceType && ( <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}> Select a scan from the list above to view its details. </Typography> )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react"; // Added useMemo
// import {
//     // Alert, // Alert wasn't used, removed
//     Box,
//     Breadcrumbs,
//     Chip, // Keep Chip for scan-level chips and severity
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon, // Kept for future icon use
//     Tooltip, // Kept Tooltip, though not used in the provided scan details
//     Divider,
//     Grid,   // Re-added Grid
//     Avatar, // Added Avatar for logo
//     Stack,  // Added Stack for layout help
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// // import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Removed as not used in provided list item


// // Assuming helpers exist
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Interfaces (Structure of your data - unchanged) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string; // Specific resource ID (e.g., 'prod-cluster-1')
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[]; // Checks passed for THIS resourceId
//     failedChecks: CheckDetail[]; // Checks failed for THIS resourceId
//     summary: { // Summary counts for THIS resourceId's scan
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string; // When the scan record was created
//     updatedAt: string; // When the scan was performed/updated
// }

// // --- Type for organizing data by resource type (unchanged) ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// // *** IMPORTANT: Update this path to the actual location of your Checkov logo image ***
// const CHECKOV_LOGO_PATH = "/images/checkov-logo.png"; // Example path


// // --- React Component ---
// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State Variables (unchanged) ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query (unchanged) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect (unchanged) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);

//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) {
//                                 acc[type] = [];
//                             }
//                             acc[type]!.push(result);
//                             acc[type]!.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to Update Selected Scan Details (unchanged) ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else {
//             setSelectedCheckovResult(null);
//         }
//      }, [selectedScanId, allCheckovData]);


//     // --- Event Handlers (unchanged) ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//              setSelectedCheckovResult(null);
//         }
//     };

//     const handleScanSelect = (scanId: string) => {
//         setSelectedScanId(scanId);
//     };

//     // --- Helper Functions (unchanged) ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//          try {
//             return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//         } catch (e) {
//             return "Invalid Date";
//         }
//     }

//      const getResourceTypeIcon = (type: string) => {
//          switch (type) {
//              case 'application': return <AppsIcon />;
//              case 'cluster': return <StorageIcon />;
//              case 'service': return <SettingsEthernetIcon />;
//              default: return <DescriptionIcon />;
//          }
//      };

//     // --- Calculate LATEST SCAN DATE for Header Card ---
//     // useMemo recalculates only when allCheckovData changes
//     const { overallLatestScanDate } = useMemo(() => {
//         // Explicitly type latestDateObj as Date | null
//         let latestDateObj: Date | null = null;

//         allCheckovData.forEach(scan => {
//             const scanDate = new Date(scan.updatedAt);
//             if (!latestDateObj || scanDate > latestDateObj) {
//                 latestDateObj = scanDate; // Find the most recent date among all scans
//             }
//         });

//         return {
//             // FIX: Correctly call toISOString() only if latestDateObj is a Date
//             overallLatestScanDate: latestDateObj ? latestDateObj : null,
//         };
//     }, [allCheckovData]); // Dependency: only recalculate if the raw data changes


//     // --- Render Logic ---
//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: { xs: "16px", md: "24px" }, bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             {/* --- 1. Page Header (unchanged) --- */}
//              <Box sx={{ mb: 3 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center">
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//              </Box>

//             {/* --- 2. Main Content Area (unchanged wrapper) --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>

//                 {/* --- Loading State (unchanged) --- */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}

//                 {/* --- Error State (unchanged) --- */}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}

//                  {/* --- Empty State (unchanged) --- */}
//                  {!isLoading && !error && allCheckovData.length === 0 && (
//                       <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}>
//                            <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                               No Checkov scan data is available to display. Run a scan to see results here.
//                           </Typography>
//                       </Paper>
//                   )}

//                 {/* --- 3. Content Display (if data loaded successfully) --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* React Fragment */}

//                         {/* ========== 3a. Checkov Header Card (unchanged structure) ========== */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}>
//                             Review the detailed results of checks executed by Checkov.
//                         </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: { xs: 2, md: 3 }, mb: 4, overflow: 'hidden' }}>
//                             <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
//                                 {/* Logo */}
//                                 <Grid item xs={12} sm="auto">
//                                     <Avatar src={CHECKOV_LOGO_PATH} alt="Checkov Logo" sx={{ width: 60, height: 60, bgcolor: '#eeeeee' }} variant="rounded" > C </Avatar>
//                                 </Grid>
//                                 {/* Text Content & Category Chip */}
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                                         Infrastructure as Code (IaC) static analysis tool for security and compliance.
//                                     </Typography>
//                                      <Box sx={{ minHeight: '26px' }} /> {/* Placeholder for consistent height */}
//                                 </Grid>
//                                 {/* Last Scanned Date */}
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                          Last Scanned On: {formatHeaderDate(overallLatestScanDate)}
//                                      </Typography>
//                                 </Grid>
//                             </Grid>
//                         </Paper>
//                         {/* ========== END: Checkov Header Card ========== */}


//                         {/* ========== 3b. Accordions for Resource Types ========== */}
//                         <Box>
//                              {/* FIX 2: Replace comment with actual JSX element */}
//                              {Object.keys(groupedData).length === 0 && !isLoading && (
//                                  <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}>
//                                     No scan results found for any resource type after loading.
//                                 </Typography>
//                             )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 if (!scans || scans.length === 0) return null;
//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} /* ... other props ... */ sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} /* ... other props ... */ sx={{ bgcolor: 'background.paper', borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', minHeight: '56px', '& .MuiAccordionSummary-content': { alignItems: 'center' } }} >
//                                             {/* <ListItemIcon>...</ListItemIcon> */}
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({scans.length} scan{scans.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => (
//                                                     <ListItemButton key={scan.id} selected={selectedScanId === scan.id} onClick={() => handleScanSelect(scan.id)} divider={index < scans.length - 1} /* ... sx props ... */ >
//                                                         <ListItemText primary={scan.resourceId} secondary={`Scanned: ${formatDate(scan.updatedAt)}`} /* ... props ... */ />
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
//                                                             <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />} label={scan.summary.passed} size="small" title="Passed checks in this scan" sx={{ bgcolor: 'success.light', color: 'success.dark', height: '22px', fontSize: '0.75rem' }} />
//                                                             <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />} label={scan.summary.failed} size="small" title="Failed checks in this scan" sx={{ bgcolor: 'error.light', color: 'error.dark', height: '22px', fontSize: '0.75rem' }}/>
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* Detailed Results Display */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}>
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}> Details for Scan: {selectedCheckovResult.resourceId} </Typography>
//                                                     {/* Passed Checks Section */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks ({selectedCheckovResult.passedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? ( selectedCheckovResult.passedChecks.map((check, index) => ( <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /></ListItem> )) ) : ( <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                     {/* Failed Checks Section */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}> <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} /> <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks ({selectedCheckovResult.failedChecks.length}) </Typography> </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? ( selectedCheckovResult.failedChecks.map((check, index) => ( <ListItem key={`fail-${check.checkId}-${index}`} divider={index < selectedCheckovResult.failedChecks.length - 1} sx={{ alignItems: 'flex-start'}}><ListItemText primary={check.checkId} secondary={check.checkName || check.description || 'No description available'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />} </ListItem> )) ) : ( <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem> )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}

//                                             {/* FIX 3: Replace comment with actual JSX element */}
//                                             {!selectedScanId && expandedAccordion === resourceType && (
//                                                 <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}>
//                                                     Select a scan from the list above to view its details.
//                                                 </Typography>
//                                             )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 ); // End return for single Accordion
//                             })} {/* End map over resource types */}
//                         </Box>
//                         {/* ========== END: Accordions for Resource Types ========== */}

//                     </>
//                 )} {/* End conditional rendering */}
//             </Box> {/* End Main Content Area Box */}
//         </Container> // End Page Container
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react"; // Added useMemo
// import {
//     // Alert, // Alert wasn't used, removed
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip, // Kept Tooltip, though not used in the provided scan details
//     Divider,
//     Grid,   // Re-added Grid
//     Avatar, // Added Avatar for logo
//     Stack,  // Added Stack for layout help
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// // import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Removed as not used in provided list item


// // Assuming helpers exist
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Interfaces (kept as provided) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         passed: number;
//         parsingErrors: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for grouped data (kept as provided) ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };

// // --- Placeholder for Logo ---
// const CHECKOV_LOGO_PATH = "/path/to/your/checkov-logo.png"; // <-- *** UPDATE THIS PATH ***


// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State (kept as provided) ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false); // For resource type accordion
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);         // For selected scan
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null); // Data for selected scan

//     // --- GraphQL Query (kept as provided) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress }
//             summary { checkovVersion failed passed } # Need summary for counts
//             createdAt
//             updatedAt # Need for sorting/latest date
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping (kept as provided) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             // Reset all states on new fetch
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);

//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                      } else {
//                         // Store raw data first
//                         setAllCheckovData(fetchedData);
//                         // Then group it for the accordions
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) {
//                                 acc[type] = [];
//                             }
//                             acc[type]!.push(result);
//                             // Sort scans within each group by date descending (most recent first)
//                             acc[type]!.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Run only on mount

//      // --- Effect to get full details for the selected scan (kept as provided) ---
//      useEffect(() => {
//         if (selectedScanId) {
//             // Find the full result object from the raw data when an ID is selected
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else {
//             // Clear details if no scan is selected
//             setSelectedCheckovResult(null);
//         }
//      }, [selectedScanId, allCheckovData]); // Re-run if selection or data changes


//     // --- Handlers (kept as provided) ---
//     // Handles expanding/collapsing the main resource type accordions
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         // If collapsing or changing the main accordion, deselect any specific scan
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//              setSelectedCheckovResult(null);
//         }
//     };

//     // Handles selecting a specific scan from the list within an accordion
//     const handleScanSelect = (scanId: string) => {
//         setSelectedScanId(scanId);
//     };

//     // --- Helper Functions (kept as provided) ---
//     // Formats date strings for display
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             // Format for scan list items
//             return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };
//     // Formats date for the header card (like image: DD/MM/YYYY)
//     const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//          try {
//             // Using a simpler format as in the image
//             return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//         } catch (e) {
//             return "Invalid Date";
//         }
//     }

//     // Returns an icon based on resource type (kept for future use)
//      const getResourceTypeIcon = (type: string) => {
//          switch (type) {
//              case 'application': return <AppsIcon />;
//              case 'cluster': return <StorageIcon />;
//              case 'service': return <SettingsEthernetIcon />;
//              default: return <DescriptionIcon />;
//          }
//      };

//     // --- Calculate Aggregated Stats for Header Card ---
//     // useMemo recalculates only when allCheckovData changes
//     const { totalPassed, totalFailed, overallLatestScanDate } = useMemo(() => {
//         let passed = 0;
//         let failed = 0;
//         let latestDate: Date | null = null;

//         allCheckovData.forEach(scan => {
//             passed += scan.summary.passed; // Sum up passed counts from each scan's summary
//             failed += scan.summary.failed; // Sum up failed counts
//             const scanDate = new Date(scan.updatedAt);
//             if (!latestDate || scanDate > latestDate) {
//                 latestDate = scanDate; // Find the most recent date among all scans
//             }
//         });

//         return {
//             totalPassed: passed,
//             totalFailed: failed,
//             overallLatestScanDate: latestDate ? latestDate : null, // Store as ISO string
//         };
//     }, [allCheckovData]);


//     // --- Render Logic ---
//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: { xs: "16px", md: "24px" }, bgcolor: "#fafafa", minHeight: "100vh" }} // Responsive padding
//         >
//             {/* --- Page Header (Breadcrumbs, Back Button, Title) --- */}
//              <Box sx={{ mb: 3 }}> {/* More margin */}
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      {/* You might want to add 'Installed Plugins' or similar here if applicable */}
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center">
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//                   {/* Description under title moved to header card */}
//              </Box>

//             {/* --- Main Content Area --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* --- Loading State --- */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30}/><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}

//                 {/* --- Error State --- */}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}

//                  {/* --- Empty State --- */}
//                  {!isLoading && !error && allCheckovData.length === 0 && (
//                       <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}>
//                            <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                               No Checkov scan data is available to display. Run a scan to see results here.
//                           </Typography>
//                       </Paper>
//                   )}

//                 {/* --- Content Display (Header Card + Accordions) --- */}
//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     <> {/* Use Fragment to return multiple root elements */}
//                         {/* ========== NEW: Checkov Header Card ========== */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}>
//                             Review the detailed results of checks executed by Checkov.
//                         </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: { xs: 2, md: 3 }, mb: 4, overflow: 'hidden' }}>
//                             <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
//                                 {/* Logo */}
//                                 <Grid item xs={12} sm="auto">
//                                     <Avatar
//                                         src={CHECKOV_LOGO_PATH} // The placeholder path
//                                         alt="Checkov Logo"
//                                         sx={{ width: 60, height: 60, bgcolor: '#eeeeee' /* Light gray background */ }}
//                                         variant="rounded"
//                                     >
//                                        C {/* Fallback text */}
//                                     </Avatar>
//                                 </Grid>
//                                 {/* Text Content & Chips */}
//                                 <Grid item xs={12} sm>
//                                     <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Checkov </Typography>
//                                     {/* Optional Subtitle like "POLICIES" can be added here */}
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5, mb: 1.5 }}>
//                                         {/* Replace with a more relevant description if possible */}
//                                         Infrastructure as Code (IaC) static analysis tool for security and compliance.
//                                     </Typography>
//                                     {/* Aggregated Passed/Failed Chips */}
//                                     <Stack direction="row" spacing={1}>
//                                         <Chip
//                                             icon={<CheckCircleOutlineIcon sx={{ fontSize: 18, mr: -0.5 }} />}
//                                             // Use the calculated totalPassed from useMemo
//                                             label={`Passed Checks: ${totalPassed}`}
//                                             size="small"
//                                             sx={{ bgcolor: '#4CAF50', color: '#ffffff', height: '26px', px: 1, borderRadius: '6px' }}
//                                         />
//                                         <Chip
//                                             icon={<ErrorOutlineIcon sx={{ fontSize: 18, mr: -0.5 }} />}
//                                             // Use the calculated totalFailed from useMemo
//                                             label={`Failed Checks: ${totalFailed}`}
//                                             size="small"
//                                             sx={{ bgcolor: '#F44336', color: '#ffffff', height: '26px', px: 1, borderRadius: '6px' }}
//                                         />
//                                     </Stack>
//                                 </Grid>
//                                 {/* Last Scanned Date */}
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, md: 0 } }}>
//                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                          {/* Use the calculated overallLatestScanDate */}
//                                          Last Scanned On: {formatHeaderDate(overallLatestScanDate)}
//                                          {/* Or show "Installed On: N/A" if you prefer */}
//                                      </Typography>
//                                 </Grid>
//                             </Grid>
//                         </Paper>
//                         {/* ========== END: Checkov Header Card ========== */}


//                         {/* ========== EXISTING: Accordions for Resource Types ========== */}
//                         <Box>
//                             {Object.keys(groupedData).length === 0 && !isLoading && (
//                                  <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}>
//                                     No scan results found for any resource type.
//                                 </Typography>
//                             )}
//                             {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                                 const scans = groupedData[resourceType];
//                                 // Only render accordion if there are scans for this type
//                                 if (!scans || scans.length === 0) return null;

//                                 const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                                 return (
//                                     <Accordion
//                                         key={resourceType}
//                                         expanded={expandedAccordion === resourceType}
//                                         onChange={handleAccordionChange(resourceType)}
//                                         disableGutters
//                                         elevation={1}
//                                         sx={{
//                                             border: "1px solid #E0E0E0",
//                                             borderRadius: "8px !important",
//                                             boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
//                                             '&:before': { display: 'none' },
//                                             mb: 2,
//                                             overflow: 'hidden', // Ensure border radius applies correctly
//                                         }}
//                                     >
//                                         <AccordionSummary
//                                             expandIcon={<ExpandMoreIcon />}
//                                             aria-controls={`${resourceType}-content`}
//                                             id={`${resourceType}-header`}
//                                             sx={{
//                                                 bgcolor: 'background.paper',
//                                                 borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none',
//                                                 minHeight: '56px',
//                                                 '& .MuiAccordionSummary-content': { alignItems: 'center' }
//                                             }}
//                                         >
//                                             {/* --- Resource Type Icon Provision ---
//                                                 We keep the function getResourceTypeIcon.
//                                                 If you want icons later, uncomment the next line.
//                                             */}
//                                              {/* <ListItemIcon sx={{ minWidth: 35, color: 'primary.main', mr: 1 }}>{getResourceTypeIcon(resourceType)}</ListItemIcon> */}

//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}>
//                                                 {resourceTypeName}
//                                             </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}>
//                                                 ({scans.length} scan{scans.length !== 1 ? 's' : ''} available)
//                                             </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Scans for this Resource Type */}
//                                             <List dense disablePadding>
//                                                 {scans.map((scan, index) => ( // Add index for divider logic
//                                                     <ListItemButton
//                                                         key={scan.id}
//                                                         selected={selectedScanId === scan.id}
//                                                         onClick={() => handleScanSelect(scan.id)}
//                                                         // Add divider except for the last item in the list
//                                                         divider={index < scans.length - 1}
//                                                         sx={{
//                                                             '&.Mui-selected': {
//                                                                 bgcolor: 'action.selected',
//                                                                 '&:hover': { bgcolor: 'action.hover' }
//                                                             },
//                                                             // Keep consistent padding left/right
//                                                             px: { xs: 2, md: 3 }, // Responsive padding
//                                                             py: 1.5, // Vertical padding
//                                                             // Indent scan list items slightly if desired
//                                                             // pl: 4
//                                                         }}
//                                                     >
//                                                         <ListItemText
//                                                             primary={scan.resourceId}
//                                                             secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }}
//                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3 }}
//                                                         />
//                                                         {/* Chips showing pass/fail for THIS specific scan */}
//                                                         <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
//                                                             <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />} label={scan.summary.passed} size="small" title="Passed" sx={{ bgcolor: 'success.light', color: 'success.dark', height: '22px', fontSize: '0.75rem' }} />
//                                                             <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />} label={scan.summary.failed} size="small" title="Failed" sx={{ bgcolor: 'error.light', color: 'error.dark', height: '22px', fontSize: '0.75rem' }}/>
//                                                         </Box>
//                                                     </ListItemButton>
//                                                 ))}
//                                             </List>

//                                             {/* --- Detailed Results Display for the SELECTED scan --- */}
//                                             {/* This section only shows up if a scan ID is selected AND it belongs to the currently expanded resource type accordion */}
//                                             {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                                 <Box sx={{ p: { xs: 2, md: 3 }, borderTop: '1px solid #eee', bgcolor: 'background.paper' }}> {/* Add padding and background */}
//                                                     {/* Section Title for Clarity */}
//                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2 }}>
//                                                        Details for Scan: {selectedCheckovResult.resourceId}
//                                                     </Typography>

//                                                     {/* Passed Checks Section for selected scan */}
//                                                     <Box mb={3}>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                             <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} />
//                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker">
//                                                                 Passed Checks ({selectedCheckovResult.passedChecks.length})
//                                                             </Typography>
//                                                         </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.passedChecks.length > 0 ? (
//                                                                 selectedCheckovResult.passedChecks.map((check, index) => (
//                                                                     <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}>
//                                                                         <ListItemText
//                                                                             primary={check.checkId}
//                                                                             secondary={check.checkName || check.description || 'No description available'}
//                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }}
//                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                         />
//                                                                     </ListItem>
//                                                                 ))
//                                                             ) : (
//                                                                 <ListItem><ListItemText primary="No passed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem>
//                                                             )}
//                                                         </List>
//                                                     </Box>

//                                                     {/* Failed Checks Section for selected scan */}
//                                                     <Box>
//                                                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                             <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} />
//                                                             <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker">
//                                                                 Failed Checks ({selectedCheckovResult.failedChecks.length})
//                                                             </Typography>
//                                                         </Box>
//                                                         <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                             {selectedCheckovResult.failedChecks.length > 0 ? (
//                                                                 selectedCheckovResult.failedChecks.map((check, index) => (
//                                                                     <ListItem
//                                                                         key={`fail-${check.checkId}-${index}`}
//                                                                         divider={index < selectedCheckovResult.failedChecks.length - 1}
//                                                                         // Removed Tooltip/Info button as requested earlier
//                                                                         // secondaryAction={...}
//                                                                         sx={{ alignItems: 'flex-start'}} // Align top if content wraps
//                                                                     >
//                                                                         <ListItemText
//                                                                             primary={check.checkId}
//                                                                             secondary={check.checkName || check.description || 'No description available'}
//                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2 }}
//                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                         />
//                                                                         {/* Severity chip if available */}
//                                                                         {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, mt: 0.5, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'} />}
//                                                                     </ListItem>
//                                                                 ))
//                                                             ) : (
//                                                                 <ListItem><ListItemText primary="No failed checks for this scan." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.85rem' }} /></ListItem>
//                                                             )}
//                                                         </List>
//                                                     </Box>
//                                                 </Box>
//                                             )}
//                                             {/* End Detailed Results Display */}

//                                             {/* Prompt to select if accordion is open but no scan selected */}
//                                             {!selectedScanId && expandedAccordion === resourceType && (
//                                                 <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}>
//                                                     Select a scan from the list above to view its details.
//                                                 </Typography>
//                                             )}
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                         {/* ========== END: Accordions for Resource Types ========== */}
//                     </>
//                 )}
//             </Box> {/* End Content Wrapper */}
//         </Container> // End Page Container
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect } from "react";
// import {
//     Alert,
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Tooltip,
//     Divider,
//     // Grid removed as we are changing the layout
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel'; // Or ErrorIcon
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps';
// import StorageIcon from '@mui/icons-material/Storage';
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For potential extra details


// // Assuming helpers exist
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Interfaces (keep as before) ---
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null; // You might need specific structure here based on actual data
// }
// interface CheckovResult {
//     id: string;
//     resourceId: string;
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         parsingErrors: number;
//         passed: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for grouped data (keep as before) ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };


// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State (keep as before) ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({});
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null);

//     // --- GraphQL Query (assuming it fetches necessary check details like checkName/description) ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             # Ensure necessary fields for display are fetched
//             passedChecks { checkId checkName resource severity description }
//             failedChecks { checkId checkName resource severity description resourceAddress } # Add fields needed for display
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping (keep as before) ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);

//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) {
//                                 acc[type] = [];
//                             }
//                             acc[type]!.push(result);
//                             acc[type]!.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to get full details (keep as before) ---
//      useEffect(() => {
//         if (selectedScanId) {
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else {
//             setSelectedCheckovResult(null);
//         }
//      }, [selectedScanId, allCheckovData]);


//     // --- Handlers (keep as before) ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//              setSelectedCheckovResult(null);
//         }
//     };

//     const handleScanSelect = (scanId: string) => {
//         setSelectedScanId(scanId);
//     };

//     // --- Helper Functions (keep as before) ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//      const getResourceTypeIcon = (type: string) => {
//          switch (type) {
//              case 'application': return <AppsIcon />;
//              case 'cluster': return <StorageIcon />;
//              case 'service': return <SettingsEthernetIcon />;
//              default: return <DescriptionIcon />;
//          }
//      };

//     // --- Render Logic ---
//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             {/* --- Header (keep as before) --- */}
//              <Box sx={{ mb: 2 }}>
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular' }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                   </Breadcrumbs>
//                  <Box display="flex" alignItems="center" mt={1.5}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//                   <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5, pl: '44px' }}>
//                      Explore Checkov scan results by resource type. Select a scan below to view details.
//                   </Typography>
//              </Box>

//             {/* --- Content Wrapper (keep as before) --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 3 }}>
//                 {/* Loading, Error, Empty States (keep as before) */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography></Paper> )}
//                 {!isLoading && !error && Object.keys(groupedData).length === 0 && allCheckovData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. </Typography></Paper> )}


//                 {!isLoading && !error && Object.keys(groupedData).length > 0 && (
//                      <Box>
//                         {/* --- Accordions for Resource Type Selection (Kept) --- */}
//                         {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                             const scans = groupedData[resourceType];
//                             if (!scans || scans.length === 0) return null;

//                             const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's';

//                             return (
//                                 <Accordion
//                                     key={resourceType}
//                                     expanded={expandedAccordion === resourceType}
//                                     onChange={handleAccordionChange(resourceType)}
//                                     disableGutters // Remove default gutters if needed
//                                     elevation={1} // Use subtle elevation
//                                     sx={{
//                                         border: "1px solid #E0E0E0",
//                                         borderRadius: "8px !important", // Adjusted border radius
//                                         boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
//                                         '&:before': { display: 'none' },
//                                         mb: 2,
//                                         overflow: 'hidden',
//                                     }}
//                                 >
//                                     <AccordionSummary
//                                         expandIcon={<ExpandMoreIcon />}
//                                         aria-controls={`${resourceType}-content`}
//                                         id={`${resourceType}-header`}
//                                         sx={{
//                                              bgcolor: 'background.paper', // Keep background consistent
//                                              borderBottom: expandedAccordion === resourceType ? '1px solid #eee' : 'none', // Divider only when expanded
//                                              minHeight: '56px', // Consistent height
//                                              '& .MuiAccordionSummary-content': { alignItems: 'center' } // Align items vertically
//                                         }}
//                                     >
//                                         <ListItemIcon sx={{ minWidth: 35, color: 'primary.main' }}>{getResourceTypeIcon(resourceType)}</ListItemIcon>
//                                         <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}>
//                                             {resourceTypeName}
//                                         </Typography>
//                                         <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}>
//                                             ({scans.length} scan{scans.length !== 1 ? 's' : ''} available)
//                                         </Typography>
//                                     </AccordionSummary>
//                                     <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}> {/* Slight off-white background */}
//                                         {/* List of Scans for this Resource Type */}
//                                         <List dense disablePadding>
//                                             {scans.map((scan) => (
//                                                 <ListItemButton
//                                                     key={scan.id}
//                                                     selected={selectedScanId === scan.id}
//                                                     onClick={() => handleScanSelect(scan.id)}
//                                                     divider
//                                                     sx={{
//                                                         '&.Mui-selected': {
//                                                             bgcolor: 'action.selected', // Highlight selected scan
//                                                             '&:hover': { bgcolor: 'action.hover' }
//                                                         },
//                                                         pl: 4 // Indent scan list items slightly
//                                                     }}
//                                                 >
//                                                     <ListItemText
//                                                         primary={scan.resourceId}
//                                                         secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                         primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem' }}
//                                                         secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem' }}
//                                                     />
//                                                       <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />} label={scan.summary.passed} size="small" title="Passed" sx={{ mr: 1, bgcolor: 'success.light', color: 'success.dark', height: '22px', fontSize: '0.75rem' }} />
//                                                       <Chip icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />} label={scan.summary.failed} size="small" title="Failed" sx={{ bgcolor: 'error.light', color: 'error.dark', height: '22px', fontSize: '0.75rem' }}/>
//                                                 </ListItemButton>
//                                             ))}
//                                         </List>

//                                         {/* ========== MODIFIED DETAILED RESULTS DISPLAY ========== */}
//                                         {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                              <Box sx={{ p: 3, borderTop: '1px solid #eee' }}>
//                                                  {/* --- Passed Checks Section --- */}
//                                                  <Box mb={3}>
//                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#e8f5e9', border: '1px solid #c8e6c9', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                          <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} />
//                                                          <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker">
//                                                             Passed Checks ({selectedCheckovResult.passedChecks.length})
//                                                          </Typography>
//                                                      </Box>
//                                                      <List dense disablePadding sx={{ border: '1px solid #c8e6c9', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                          {selectedCheckovResult.passedChecks.length > 0 ? (
//                                                              selectedCheckovResult.passedChecks.map((check, index) => (
//                                                                  <ListItem key={`pass-${check.checkId}-${index}`} divider={index < selectedCheckovResult.passedChecks.length - 1}>
//                                                                      <ListItemText
//                                                                         primary={check.checkId}
//                                                                         secondary={check.checkName || check.description || 'No description available'} // Show name or description
//                                                                         primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', mb: 0.2 }}
//                                                                         secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary' }}
//                                                                      />
//                                                                      {/* Optional: Add severity chip or other details */}
//                                                                  </ListItem>
//                                                              ))
//                                                          ) : (
//                                                             <ListItem><ListItemText primary="No passed checks." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{fontSize: '0.85rem'}} /></ListItem>
//                                                          )}
//                                                      </List>
//                                                  </Box>

//                                                  {/* --- Failed Checks Section --- */}
//                                                   <Box>
//                                                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: '#ffebee', border: '1px solid #ffcdd2', borderBottom: 'none', borderRadius: '6px 6px 0 0' }}>
//                                                           <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} />
//                                                           <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker">
//                                                              Failed Checks ({selectedCheckovResult.failedChecks.length})
//                                                           </Typography>
//                                                       </Box>
//                                                       <List dense disablePadding sx={{ border: '1px solid #ffcdd2', borderRadius: '0 0 6px 6px', bgcolor: '#ffffff', maxHeight: 350, overflow: 'auto' }}>
//                                                           {selectedCheckovResult.failedChecks.length > 0 ? (
//                                                               selectedCheckovResult.failedChecks.map((check, index) => (
//                                                                   <ListItem
//                                                                     key={`fail-${check.checkId}-${index}`}
//                                                                     divider={index < selectedCheckovResult.failedChecks.length - 1}
//                                                                     secondaryAction={
//                                                                         // Placeholder for "X clusters violating..." - Adapt based on your data
//                                                                         check.resourceAddress ? (
//                                                                             <Tooltip title={`Resource: ${check.resourceAddress}`}>
//                                                                                 <IconButton edge="end" size="small" sx={{ color: 'text.secondary' }}>
//                                                                                     <InfoOutlinedIcon fontSize="small" />
//                                                                                 </IconButton>
//                                                                             </Tooltip>
//                                                                         ) : null
//                                                                     }
//                                                                    >
//                                                                       <ListItemText
//                                                                           primary={check.checkId}
//                                                                           secondary={check.checkName || check.description || 'No description available'}
//                                                                           primaryTypographyProps={{fontFamily: 'Gilroy-Medium', fontSize: '0.9rem', color: 'error.dark', mb: 0.2}}
//                                                                           secondaryTypographyProps={{fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary'}}
//                                                                        />
//                                                                        {/* Example: Add severity chip */}
//                                                                        {check.severity && <Chip label={check.severity} size="small" sx={{ ml: 2, height: '18px', fontSize: '0.7rem' }} color={check.severity === 'HIGH' ? 'error' : check.severity === 'MEDIUM' ? 'warning' : 'info'}/>}

//                                                                   </ListItem>
//                                                               ))
//                                                           ) : (
//                                                               <ListItem><ListItemText primary="No failed checks." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{fontSize: '0.85rem'}} /></ListItem>
//                                                           )}
//                                                       </List>
//                                                   </Box>
//                                              </Box>
//                                         )}
//                                         {/* ========== END MODIFIED DETAILS ========== */}

//                                         {/* Prompt to select if accordion is open but no scan selected */}
//                                         {!selectedScanId && expandedAccordion === resourceType && (
//                                             <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', borderTop: '1px solid #eee' }}>
//                                                 Select a scan from the list above to view its details.
//                                             </Typography>
//                                         )}
//                                     </AccordionDetails>
//                                 </Accordion>
//                             );
//                         })}
//                     </Box>
//                 )}
//             </Box> {/* End Centered Content Wrapper */}
//         </Container> // End Page Container
//     );
// };

// export default CheckovDetails;


// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Alert,
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton, // Use Button for clickable list items
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion, // Import Accordion components
//     AccordionSummary,
//     AccordionDetails,
//     ListItemIcon,
//     Divider,
//     Grid, // Keep Grid if needed for layout within AccordionDetails
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Default Accordion icon
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// import DescriptionIcon from '@mui/icons-material/Description';
// import AppsIcon from '@mui/icons-material/Apps'; // Icon for Applications
// import StorageIcon from '@mui/icons-material/Storage'; // Icon for Clusters (example)
// import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet'; // Icon for Services (example)


// // Assuming helpers exist
// import queryGraphql from "../../helpers/ApiInterface";

// // --- Interfaces (reuse from previous versions) ---
// interface CheckDetail { // ... same as before ...
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }
// interface CheckovResult { // ... same as before ...
//     id: string; // Maps to _id.$oid
//     resourceId: string; // Maps to resourceId.$oid
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         parsingErrors: number;
//         passed: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // --- Type for grouped data ---
// type GroupedCheckovData = {
//     [key in "application" | "cluster" | "service"]?: CheckovResult[];
// };


// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // --- State ---
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]); // Raw fetched data
//     const [groupedData, setGroupedData] = useState<GroupedCheckovData>({}); // Data grouped by resourceType
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false); // Track expanded accordion panel (resource type)
//     const [selectedScanId, setSelectedScanId] = useState<string | null>(null); // Unique ID (_id.$oid) of the selected scan
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null); // Full data for the selected scan

//     // --- GraphQL Query ---
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity }
//             failedChecks { checkId checkName resource severity }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setSelectedScanId(null);
//             setSelectedCheckovResult(null);

//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          setAllCheckovData([]);
//                          setGroupedData({});
//                          // No error needed here, just show empty state later
//                      } else {
//                         setAllCheckovData(fetchedData);
//                         // Group data by resourceType
//                         const groups: GroupedCheckovData = fetchedData.reduce((acc, result) => {
//                             const type = result.resourceType;
//                             if (!acc[type]) {
//                                 acc[type] = [];
//                             }
//                             // Sort scans within each group by date descending
//                             acc[type]!.push(result);
//                             acc[type]!.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
//                             return acc;
//                         }, {} as GroupedCheckovData);
//                         setGroupedData(groups);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//      // --- Effect to get full details when a scan ID is selected ---
//      useEffect(() => {
//         if (selectedScanId) {
//             // Find the full result object from the raw data
//             const result = allCheckovData.find(item => item.id === selectedScanId);
//             setSelectedCheckovResult(result || null);
//         } else {
//             setSelectedCheckovResult(null);
//         }
//      }, [selectedScanId, allCheckovData]);


//     // --- Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         const newExpanded = isExpanded ? panel : false;
//         setExpandedAccordion(newExpanded);
//         // Reset selection when changing panels
//         if (!isExpanded || (isExpanded && panel !== selectedCheckovResult?.resourceType)) {
//              setSelectedScanId(null);
//              setSelectedCheckovResult(null);
//         }
//     };

//     const handleScanSelect = (scanId: string) => {
//         setSelectedScanId(scanId);
//     };

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             return new Date(isoString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//      const getResourceTypeIcon = (type: string) => {
//          switch (type) {
//              case 'application': return <AppsIcon />;
//              case 'cluster': return <StorageIcon />;
//              case 'service': return <SettingsEthernetIcon />;
//              default: return <DescriptionIcon />;
//          }
//      };


//     // --- Render Logic ---
//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             {/* --- Header (Left-Aligned) --- */}
//              <Box sx={{ mb: 2 }}>
//                  {/* ... breadcrumbs ... */}
//                   <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular' }}>
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                  </Breadcrumbs>
//                  <Box display="flex" alignItems="center" mt={1.5}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                  </Box>
//                   <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5, pl: '44px' }}>
//                      Explore Checkov scan results by resource type.
//                   </Typography>
//              </Box>

//             {/* --- Content Wrapper (Centered Block) --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 3 }}>
//                 {isLoading && ( /* Loading State */
//                     <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}>
//                         <CircularProgress /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography>
//                     </Paper>
//                 )}

//                 {!isLoading && error && ( /* Error State */
//                      <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}>
//                          <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography>
//                      </Paper>
//                 )}

//                 {!isLoading && !error && Object.keys(groupedData).length === 0 && allCheckovData.length === 0 && ( /* Empty State */
//                       <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid #E0E0E0", textAlign: 'center' }}>
//                           <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Checkov scan data is available to display. </Typography>
//                       </Paper>
//                   )}


//                 {!isLoading && !error && Object.keys(groupedData).length > 0 && (
//                     // --- Main Content Area with Accordions ---
//                     // No single outer Paper card needed here, Accordions serve as the main containers
//                      <Box>
//                         {(['application', 'cluster', 'service'] as const).map((resourceType) => {
//                             const scans = groupedData[resourceType];
//                             if (!scans || scans.length === 0) return null; // Don't render accordion if no scans for this type

//                             const resourceTypeName = resourceType.charAt(0).toUpperCase() + resourceType.slice(1) + 's'; // Capitalize (e.g., Applications)

//                             return (
//                                 <Accordion
//                                     key={resourceType}
//                                     expanded={expandedAccordion === resourceType}
//                                     onChange={handleAccordionChange(resourceType)}
//                                     sx={{
//                                         border: "1px solid #E0E0E0",
//                                         borderRadius: "12px !important", // Override default square corners
//                                         boxShadow: expandedAccordion === resourceType ? 3 : 1, // More shadow when expanded
//                                         '&:before': { display: 'none' }, // Remove default top border
//                                         mb: 2, // Margin between accordions
//                                         overflow: 'hidden' // Ensure rounded corners apply correctly
//                                     }}
//                                 >
//                                     <AccordionSummary
//                                         expandIcon={<ExpandMoreIcon />}
//                                         aria-controls={`${resourceType}-content`}
//                                         id={`${resourceType}-header`}
//                                         sx={{
//                                              bgcolor: expandedAccordion === resourceType ? 'action.hover' : 'background.paper',
//                                              borderTopLeftRadius: '12px', // Ensure consistent radius
//                                              borderTopRightRadius: '12px'
//                                         }}
//                                     >
//                                         <ListItemIcon sx={{ minWidth: 35, color: 'primary.main' }}>{getResourceTypeIcon(resourceType)}</ListItemIcon>
//                                         <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}>
//                                             {resourceTypeName}
//                                         </Typography>
//                                         <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular' }}>
//                                             ({scans.length} scan{scans.length !== 1 ? 's' : ''} available)
//                                         </Typography>
//                                     </AccordionSummary>
//                                     <AccordionDetails sx={{ p: 0 }}> {/* Remove padding to allow full-width list/details */}
//                                         {/* List of Scans for this Resource Type */}
//                                         <List dense disablePadding sx={{ borderBottom: '1px solid #eee' }}>
//                                             {scans.map((scan) => (
//                                                 <ListItemButton
//                                                     key={scan.id}
//                                                     selected={selectedScanId === scan.id}
//                                                     onClick={() => handleScanSelect(scan.id)}
//                                                     divider // Add divider between scan items
//                                                 >
//                                                     <ListItemText
//                                                         primary={scan.resourceId}
//                                                         secondary={`Scanned: ${formatDate(scan.updatedAt)}`}
//                                                         primaryTypographyProps={{ fontFamily: 'Gilroy-Medium' }}
//                                                         secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem' }}
//                                                     />
//                                                      {/* Optionally add Pass/Fail counts as chips here */}
//                                                       <Chip
//                                                           icon={<CheckCircleOutlineIcon fontSize="small" />}
//                                                           label={scan.summary.passed}
//                                                           size="small" title="Passed"
//                                                           sx={{ mr: 1, bgcolor: 'success.light', color: 'success.dark', height: '20px' }}
//                                                       />
//                                                       <Chip
//                                                            icon={<ErrorOutlineIcon fontSize="small" />}
//                                                           label={scan.summary.failed}
//                                                           size="small" title="Failed"
//                                                           sx={{ bgcolor: 'error.light', color: 'error.dark', height: '20px' }}
//                                                       />
//                                                 </ListItemButton>
//                                             ))}
//                                         </List>

//                                         {/* Detailed Results Display (Conditional) */}
//                                         {selectedCheckovResult && selectedCheckovResult.resourceType === resourceType && (
//                                              <Box sx={{ pt: 2, px: 2.5, pb: 2.5 }}> {/* Add padding around details */}
//                                                  <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1.1rem', mb: 2 }}>
//                                                     Scan Details for: {selectedCheckovResult.resourceId}
//                                                  </Typography>

//                                                  {/* Use Grid for side-by-side layout of Pass/Fail */}
//                                                  <Grid container spacing={2}>
//                                                      {/* Passed Checks */}
//                                                      <Grid item xs={12} md={6}>
//                                                          <Box sx={{ bgcolor: '#e8f5e9', display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '4px 4px 0 0' }}>
//                                                              <CheckCircleIcon sx={{ fontSize: 18, color: 'success.dark' }} />
//                                                              <Typography fontFamily="Gilroy-SemiBold" fontSize="13px" color="success.darker">
//                                                                 Passed Checks ({selectedCheckovResult.passedChecks.length})
//                                                              </Typography>
//                                                          </Box>
//                                                          <List dense disablePadding sx={{ border: '1px solid #e8f5e9', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: 300, overflow: 'auto' }}>
//                                                              {selectedCheckovResult.passedChecks.length > 0 ? (
//                                                                  selectedCheckovResult.passedChecks.map((check, index) => (
//                                                                      <ListItem key={`pass-${check.checkId}-${index}`} divider>
//                                                                          <ListItemText primary={check.checkId} secondary={check.checkName} primaryTypographyProps={{fontFamily: 'Gilroy-Medium', fontSize: '0.85rem'}} secondaryTypographyProps={{fontFamily: 'Gilroy-Regular', fontSize: '0.8rem'}} />
//                                                                      </ListItem>
//                                                                  ))
//                                                              ) : (<ListItem><ListItemText primary="No passed checks." primaryTypographyProps={{fontSize: '0.85rem', color: 'text.secondary'}}/></ListItem>)}
//                                                          </List>
//                                                      </Grid>

//                                                      {/* Failed Checks */}
//                                                       <Grid item xs={12} md={6}>
//                                                           <Box sx={{ bgcolor: '#ffebee', display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: '4px 4px 0 0' }}>
//                                                               <CancelIcon sx={{ fontSize: 18, color: 'error.dark' }} />
//                                                               <Typography fontFamily="Gilroy-SemiBold" fontSize="13px" color="error.darker">
//                                                                  Failed Checks ({selectedCheckovResult.failedChecks.length})
//                                                               </Typography>
//                                                           </Box>
//                                                           <List dense disablePadding sx={{ border: '1px solid #ffebee', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: 300, overflow: 'auto' }}>
//                                                               {selectedCheckovResult.failedChecks.length > 0 ? (
//                                                                   selectedCheckovResult.failedChecks.map((check, index) => (
//                                                                       <ListItem key={`fail-${check.checkId}-${index}`} divider>
//                                                                           <ListItemText primary={check.checkId} secondary={check.checkName} primaryTypographyProps={{fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', color: 'error.dark'}} secondaryTypographyProps={{fontFamily: 'Gilroy-Regular', fontSize: '0.8rem'}} />
//                                                                       </ListItem>
//                                                                   ))
//                                                               ) : (<ListItem><ListItemText primary="No failed checks." primaryTypographyProps={{fontSize: '0.85rem', color: 'text.secondary'}}/></ListItem>)}
//                                                           </List>
//                                                       </Grid>
//                                                  </Grid>
//                                              </Box>
//                                         )}
//                                         {/* Prompt to select if accordion is open but no scan selected */}
//                                         {!selectedScanId && expandedAccordion === resourceType && (
//                                             <Typography sx={{ p: 2.5, textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular' }}>
//                                                 Select a scan from the list above to view details.
//                                             </Typography>
//                                         )}
//                                     </AccordionDetails>
//                                 </Accordion>
//                             );
//                         })}
//                     </Box>
//                 )}
//             </Box> {/* End Centered Content Wrapper */}
//         </Container> // End Page Container
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Alert,
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Container,
//     Divider,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemText,
//     Paper,
//     Typography,
//     ListItemIcon,
//     Grid, // Using Grid for dropdown layout
//     Autocomplete, // Bring back Autocomplete
//     TextField // Needed for Autocomplete
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
// import DescriptionIcon from '@mui/icons-material/Description';
// import FilterListIcon from '@mui/icons-material/FilterList'; // Icon for filter section

// // Assuming helpers exist
// import queryGraphql from "../../helpers/ApiInterface";

// // Interfaces (reuse from previous versions)
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }

// interface CheckovResult {
//     id: string; // Maps to _id.$oid
//     resourceId: string; // Maps to resourceId.$oid
//     resourceType: "application" | "cluster" | "service";
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         parsingErrors: number;
//         passed: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string;
//     updatedAt: string;
// }

// // Interface for dropdown options
// interface ResourceIdOption {
//     label: string; // The resourceId string for display
//     id: string;    // The unique scan ID (_id.$oid) for lookup
// }


// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // State variables - Combining both approaches
//     const [allCheckovData, setAllCheckovData] = useState<CheckovResult[]>([]);
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);

//     // State for dropdowns and selected result
//     const [selectedResourceType, setSelectedResourceType] = useState<string | null>(null);
//     const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null); // Stores the unique scan ID (_id.$oid)
//     const [resourceIdOptions, setResourceIdOptions] = useState<ResourceIdOption[]>([]);
//     const [selectedCheckovResult, setSelectedCheckovResult] = useState<CheckovResult | null>(null); // Holds the result matching selections

//     // GraphQL Query (same as before)
//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { checkId checkName resource severity }
//             failedChecks { checkId checkName resource severity }
//             summary { checkovVersion failed passed }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // Fetch data on component mount
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllCheckovData([]); // Reset data
//             setSelectedResourceType(null); // Reset selections
//             setSelectedResourceId(null);
//             setSelectedCheckovResult(null);

//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData)) {
//                      if (fetchedData.length === 0) {
//                          // Handle case where API returns empty array - show message later
//                          setAllCheckovData([]);
//                      } else {
//                          // Store all fetched data
//                         setAllCheckovData(fetchedData);
//                      }
//                 } else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // --- Effects for handling dropdown logic ---

//     // Calculate unique resource types for the first dropdown
//     const resourceTypeOptions = useMemo(() => {
//         const types = new Set(allCheckovData.map(item => item.resourceType));
//         return Array.from(types).sort(); // Sort alphabetically
//     }, [allCheckovData]);

//     // Update resource ID options when resource type changes
//     useEffect(() => {
//         let filteredOptions: ResourceIdOption[] = [];
//         if (selectedResourceType) {
//             const filteredScans = allCheckovData
//                 .filter(item => item.resourceType === selectedResourceType)
//                 // Sort scans for the same resource, e.g., by date descending
//                 .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

//             // Create options, potentially adding date to label for clarity if multiple scans exist for same resourceId
//              filteredOptions = filteredScans.map(item => ({
//                 label: `${item.resourceId} (${formatDate(item.updatedAt)})`, // Display resource ID + date
//                 id: item.id // Use the unique scan ID
//             }));

//             // Basic deduplication based on label if needed, though date makes them unique
//             // const uniqueOptions = Array.from(new Map(filteredOptions.map(item => [item.label, item])).values());
//             // setResourceIdOptions(uniqueOptions);

//         }
//         setResourceIdOptions(filteredOptions);

//         // Reset subsequent selections when type changes
//         setSelectedResourceId(null);
//         setSelectedCheckovResult(null);
//     }, [selectedResourceType, allCheckovData]);

//     // Find and set the specific Checkov result when resource ID (scan ID) is selected
//     useEffect(() => {
//         if (selectedResourceId) {
//             const result = allCheckovData.find(item => item.id === selectedResourceId);
//             setSelectedCheckovResult(result || null);
//         } else {
//             setSelectedCheckovResult(null);
//         }
//     }, [selectedResourceId, allCheckovData]);

//     // --- Helper Functions ---
//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             // Using a slightly more informative date format for dropdown label
//             return new Date(isoString).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); // e.g., Apr 8, 2025
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };
//      const formatHeaderDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             return new Date(isoString).toLocaleDateString(); // e.g., 4/8/2025
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//      // --- Handlers for Autocomplete changes ---
//     const handleResourceTypeChange = (event: any, newValue: string | null) => {
//         setSelectedResourceType(newValue);
//     };

//     const handleResourceIdChange = (event: any, newValue: ResourceIdOption | null) => {
//         setSelectedResourceId(newValue ? newValue.id : null); // Store the unique scan ID
//     };


//     // --- Render Logic ---
//     return (
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             {/* --- Header (Left-Aligned - same as before) --- */}
//             <Box sx={{ mb: 2 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular' }}>
//                     {/* ... breadcrumbs */}
//                      <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                      <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Checkov </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1.5}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate(-1)} sx={{ mr: 1, p: 0.5 }} >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Checkov </Typography>
//                 </Box>
//                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5, pl: '44px' }}>
//                     Select a resource type and specific scan to review detailed results.
//                  </Typography>
//             </Box>

//             {/* --- Content Wrapper (Centered Block - same as before) --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 3 }}>
//                 {isLoading && (
//                     <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}>
//                         <CircularProgress />
//                         <Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Data...</Typography>
//                     </Paper>
//                 )}

//                 {!isLoading && error && (
//                      <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}>
//                         <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> {error} </Typography>
//                      </Paper>
//                 )}

//                  {!isLoading && !error && allCheckovData.length === 0 && (
//                      // --- Case where data array is empty after successful fetch ---
//                       <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid #E0E0E0", textAlign: 'center' }}>
//                           <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                               No Checkov scan data is available to display.
//                           </Typography>
//                       </Paper>
//                   )}


//                 {!isLoading && !error && allCheckovData.length > 0 && (
//                     // --- Main Content Card ---
//                     <Paper elevation={1} sx={{ borderRadius: "12px", overflow: 'hidden', border: "1px solid #E0E0E0" }}>
//                         {/* === Card Header: Displays selected scan info === */}
//                         <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', flexWrap: 'wrap', gap: 2, minHeight: '80px' /* Ensure height even when empty */ }}>
//                              {/* Left side: Icon, Name, Version, Chips */}
//                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                                 {/* Placeholder Icon */}
//                                 <Box sx={{ bgcolor: 'grey.300', color: 'text.secondary', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                                     <DescriptionIcon />
//                                 </Box>
//                                 <Box>
//                                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
//                                         <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                                            Checkov Scan Details
//                                         </Typography>
//                                         {selectedCheckovResult && ( // Show version only when selected
//                                             <Chip
//                                                 label={`v${selectedCheckovResult.summary.checkovVersion || 'N/A'}`}
//                                                 size="small" variant="outlined"
//                                                 sx={{ height: '20px', lineHeight: '1', borderRadius: '4px', fontFamily: 'Gilroy-Medium', fontSize: '0.7rem' }}
//                                             />
//                                         )}
//                                     </Box>
//                                      {/* Summary Chips - Update based on selection */}
//                                     {selectedCheckovResult ? (
//                                         <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//                                             <Chip
//                                                 icon={<CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main !important' }} />}
//                                                 label={`Passed: ${selectedCheckovResult.summary.passed}`}
//                                                 size="small"
//                                                 sx={{ bgcolor: '#e8f5e9', color: 'success.dark', fontFamily: 'Gilroy-Medium', fontSize: '0.75rem', '.MuiChip-icon': { ml: '8px' } }}
//                                             />
//                                             <Chip
//                                                 icon={<ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main !important' }} />}
//                                                 label={`Failed: ${selectedCheckovResult.summary.failed}`}
//                                                 size="small"
//                                                 sx={{ bgcolor: '#ffebee', color: 'error.dark', fontFamily: 'Gilroy-Medium', fontSize: '0.75rem', '.MuiChip-icon': { ml: '8px' } }}
//                                             />
//                                         </Box>
//                                      ) : (
//                                           <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 1.5 }}>
//                                             Select a scan below to see details.
//                                           </Typography>
//                                      )}
//                                 </Box>
//                             </Box>
//                             {/* Right side: Selected Scan Date */}
//                              {selectedCheckovResult && (
//                                 <Box sx={{ textAlign: 'right', pt: 0.5 }}>
//                                     <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                         Scan Date:
//                                     </Typography>
//                                     <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Medium' }}>
//                                         {formatHeaderDate(selectedCheckovResult.updatedAt)}
//                                     </Typography>
//                                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', display: 'block', mt: 0.5 }}>
//                                         Resource ID: {selectedCheckovResult.resourceId}
//                                     </Typography>
//                                 </Box>
//                             )}
//                         </Box>

//                         {/* === Card Body: Filters Section === */}
//                          <Box sx={{ bgcolor: '#f9f9f9' }}> {/* Section Header */}
//                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, pl: 2.5 }}>
//                                  <FilterListIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
//                                  <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="text.secondary">
//                                     Select Scan Result
//                                  </Typography>
//                              </Box>
//                         </Box>
//                         {/* Dropdown Filters */}
//                          <Box sx={{ p: 2.5 }}>
//                             <Grid container spacing={2}>
//                                 <Grid item xs={12} sm={6}>
//                                     <Autocomplete
//                                         options={resourceTypeOptions}
//                                         value={selectedResourceType}
//                                         onChange={handleResourceTypeChange}
//                                         renderInput={(params) => (
//                                             <TextField {...params} label="Resource Type" size="small" variant="outlined" />
//                                         )}
//                                         data-testid="resource-type-select"
//                                     />
//                                 </Grid>
//                                 <Grid item xs={12} sm={6}>
//                                     <Autocomplete
//                                         options={resourceIdOptions}
//                                         getOptionLabel={(option) => option.label} // Display resourceId + date
//                                         isOptionEqualToValue={(option, value) => option.id === value.id} // Compare by unique scan ID
//                                         value={resourceIdOptions.find(option => option.id === selectedResourceId) || null}
//                                         onChange={handleResourceIdChange}
//                                         disabled={!selectedResourceType || resourceIdOptions.length === 0}
//                                         renderInput={(params) => (
//                                             <TextField {...params} label="Resource ID / Scan Date" size="small" variant="outlined" />
//                                         )}
//                                         data-testid="resource-id-select"
//                                     />
//                                 </Grid>
//                             </Grid>
//                          </Box>

//                         {/* === Card Body: Results Display (Conditional) === */}
//                         {selectedCheckovResult && (
//                             <>
//                                 <Divider />
//                                 {/* --- Passed Checks Section --- */}
//                                 <Box sx={{ bgcolor: '#e8f5e9' }}> {/* Header */}
//                                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, pl: 2.5 }}>
//                                         <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} />
//                                         <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker"> Passed Checks </Typography>
//                                     </Box>
//                                 </Box>
//                                 <List disablePadding sx={{ bgcolor: 'background.paper' }}>
//                                     {selectedCheckovResult.passedChecks.length > 0 ? (
//                                         selectedCheckovResult.passedChecks.map((check, index) => (
//                                             <ListItem key={`pass-${check.checkId}-${index}`} divider sx={{ py: 1.5, px: 2.5 }}>
//                                                 <ListItemText
//                                                     primary={ <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'Gilroy-Medium' }}> {check.checkId} </Typography> }
//                                                     secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {check.checkName} {/* | Resource: ${check.resource} */} </Typography> }
//                                                     primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'body2' }}
//                                                 />
//                                             </ListItem>
//                                         ))
//                                     ) : (
//                                         <ListItem sx={{ py: 1.5, px: 2.5 }}> <ListItemText primary="No passed checks in this scan." primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontFamily: 'Gilroy-Regular' }} /> </ListItem>
//                                     )}
//                                 </List>

//                                 {/* --- Failed Checks Section --- */}
//                                 {selectedCheckovResult.failedChecks.length > 0 && (
//                                     <>
//                                         <Box sx={{ bgcolor: '#ffebee' }}> {/* Header */}
//                                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, pl: 2.5 }}>
//                                                 <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} />
//                                                 <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker"> Failed Checks </Typography>
//                                             </Box>
//                                         </Box>
//                                         <List disablePadding sx={{ bgcolor: 'background.paper' }}>
//                                             {selectedCheckovResult.failedChecks.map((check, index) => (
//                                                 <ListItem key={`fail-${check.checkId}-${index}`} divider sx={{ py: 1.5, px: 2.5 }}>
//                                                     <ListItemText
//                                                          primary={ <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'Gilroy-Medium' }}> {check.checkId} </Typography> }
//                                                          secondary={ <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> {check.checkName} {/* | Severity: ${check.severity || 'N/A'} | Resource: ${check.resource} */} </Typography> }
//                                                          primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'body2' }}
//                                                     />
//                                                 </ListItem>
//                                             ))}
//                                         </List>
//                                     </>
//                                 )}
//                             </>
//                         )} {/* End conditional results display */}

//                     </Paper> // End Main Content Card
//                 )}
//             </Box> {/* End Centered Content Wrapper */}
//         </Container> // End Page Container
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Alert,
//     Box,
//     Breadcrumbs,
//     Chip, // For summary chips
//     CircularProgress,
//     Container,
//     Divider, // Can use this or ListItem divider
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemText,
//     Paper, // Main card container
//     Typography,
//     ListItemIcon, // To add icons to section headers/list items
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Icon for passed checks section
// import CancelIcon from '@mui/icons-material/Cancel'; // Icon for failed checks section
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Icon for passed count chip
// import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; // Icon for failed count chip
// import DescriptionIcon from '@mui/icons-material/Description'; // Placeholder icon for Checkov

// // Assuming these helpers exist based on your original code
// import queryGraphql from "../../helpers/ApiInterface";
// // import { useAuth } from "../../helpers/AuthProvider";
// // import MainDrawer from "../MainDrawer"; // Commented out as per your snippet

// // Interfaces (reuse from previous version)
// interface CheckDetail {
//     checkId: string;
//     checkName: string;
//     severity: string | null;
//     resource: string;
//     checkClass: string;
//     resourceAddress: string;
//     description: string | null;
//     shortDescription: string | null;
//     vulnerabilityDetails: any | null;
// }

// interface CheckovResult {
//     id: string;
//     resourceId: string; // Still fetched, could be shown if needed
//     resourceType: "application" | "cluster" | "service"; // Still fetched
//     passedChecks: CheckDetail[];
//     failedChecks: CheckDetail[];
//     summary: {
//         checkovVersion: string;
//         failed: number;
//         parsingErrors: number;
//         passed: number;
//         resourceCount: number;
//         skipped: number;
//     };
//     createdAt: string; // Date string
//     updatedAt: string; // Date string
// }

// const CheckovDetails: React.FC = () => {
//     const navigate = useNavigate();

//     // State variables
//     const [displayResult, setDisplayResult] = useState<CheckovResult | null>(null);
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);

//     const getCheckovResultsQuery = `
//         query GetCheckovResults {
//           getCheckovResults {
//             id
//             resourceId
//             resourceType
//             passedChecks { # Fetch details needed for display
//               checkId
//               checkName
//               resource
//               # Add other fields like description if you want to show them
//             }
//             failedChecks { # Fetch details needed for display
//               checkId
//               checkName
//               resource
//               severity
//               # Add other fields like description if you want to show them
//             }
//             summary {
//               checkovVersion
//               failed
//               passed
//               # Fetch other summary fields if needed
//             }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // Fetch data on component mount
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setDisplayResult(null); // Reset display result
//             try {
//                 const response = await queryGraphql(getCheckovResultsQuery);
//                 const fetchedData: CheckovResult[] = response?.getCheckovResults;

//                 if (fetchedData && Array.isArray(fetchedData) && fetchedData.length > 0) {
//                     // Sort by updatedAt descending to get the most recent
//                     const sortedData = [...fetchedData].sort((a, b) =>
//                         new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
//                     );
//                     setDisplayResult(sortedData[0]); // Display the latest result
//                 } else if (fetchedData && fetchedData.length === 0) {
//                      setError("No Checkov scan results found.");
//                      setDisplayResult(null);
//                 }
//                 else {
//                     console.error("Checkov data not found or structure mismatch:", response);
//                     setError("Could not fetch Checkov data or the format is incorrect.");
//                     setDisplayResult(null);
//                 }
//             } catch (err: any) {
//                 console.error("Error fetching Checkov data:", err);
//                 setError(err.message || "An unexpected error occurred while fetching data.");
//                 setDisplayResult(null);
//             } finally {
//                 setIsLoading(false);
//             }
//         };

//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Runs once on mount

//     const formatDate = (isoString: string | null): string => {
//         if (!isoString) return "N/A";
//         try {
//             return new Date(isoString).toLocaleDateString(); // e.g., 4/8/2025
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//     return (
//         // Use Container structure similar to GCPCostAgent for overall page layout
//         <Container
//             disableGutters
//             maxWidth={false}
//             sx={{ padding: "24px", bgcolor: "#fafafa", minHeight: "100vh" }}
//         >
//             {/* --- Header (Left-Aligned) --- */}
//             <Box sx={{ mb: 2 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular' }}>
//                     <Link
//                         underline="hover"
//                         color="inherit"
//                         onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} // Adjust link as needed
//                         sx={{ cursor: 'pointer' }}
//                     >
//                         Integrations
//                     </Link>
//                     {/* Add more levels if needed, e.g., Installed Plugins */}
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}>
//                         Checkov
//                     </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1.5}>
//                     <IconButton
//                         edge="start"
//                         aria-label="back"
//                         color="inherit"
//                         onClick={() => navigate(-1)} // Go back
//                         sx={{ mr: 1, p: 0.5 }}
//                     >
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px">
//                         Checkov
//                     </Typography>
//                 </Box>
//                  <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5, pl: '44px' }}> {/* Indent slightly */}
//                     Review the detailed results of checks executed by Checkov.
//                  </Typography>
//             </Box>

//             {/* --- Content Wrapper (Centered Block) --- */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto', mt: 3 }}>
//                 {isLoading && (
//                     // --- Loading State ---
//                     <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}>
//                         <CircularProgress />
//                         <Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Checkov Results...</Typography>
//                     </Paper>
//                 )}

//                 {!isLoading && error && (
//                      // --- Error State ---
//                      <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}>
//                         <Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}>
//                             {error}
//                         </Typography>
//                      </Paper>
//                 )}

//                 {!isLoading && !error && displayResult && (
//                     // --- Data Loaded - Display Card ---
//                     <Paper elevation={1} sx={{ borderRadius: "12px", overflow: 'hidden', border: "1px solid #E0E0E0" }}>
//                         {/* === Card Header (Similar to Image) === */}
//                         <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', flexWrap: 'wrap', gap: 2 }}>
//                             {/* Left side: Icon, Name, Version, Chips */}
//                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
//                                 {/* Placeholder Icon - Replace with actual Checkov logo if available */}
//                                 <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                                     <DescriptionIcon />
//                                 </Box>
//                                 <Box>
//                                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
//                                         <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                                             Checkov
//                                         </Typography>
//                                         <Chip
//                                            label={`v${displayResult.summary.checkovVersion || 'N/A'}`}
//                                            size="small"
//                                            variant="outlined" // Different style for version
//                                            sx={{ height: '20px', lineHeight: '1', borderRadius: '4px', fontFamily: 'Gilroy-Medium', fontSize: '0.7rem' }}
//                                         />
//                                     </Box>
//                                     {/* Add description if needed/available */}
//                                     {/* <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mt: 0.5 }}>
//                                         Infrastructure as Code static analysis.
//                                     </Typography> */}
//                                     {/* Summary Chips */}
//                                     <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
//                                          <Chip
//                                             icon={<CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main !important' }} />}
//                                             label={`Passed Checks: ${displayResult.summary.passed}`}
//                                             size="small"
//                                             sx={{ bgcolor: '#e8f5e9', color: 'success.dark', fontFamily: 'Gilroy-Medium', fontSize: '0.75rem', '.MuiChip-icon': { ml: '8px' } }} // Adjusted icon margin
//                                          />
//                                          <Chip
//                                              icon={<ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main !important' }} />}
//                                             label={`Failed Checks: ${displayResult.summary.failed}`}
//                                             size="small"
//                                             sx={{ bgcolor: '#ffebee', color: 'error.dark', fontFamily: 'Gilroy-Medium', fontSize: '0.75rem', '.MuiChip-icon': { ml: '8px' } }}
//                                          />
//                                     </Box>
//                                 </Box>
//                             </Box>
//                             {/* Right side: Install/Update Date */}
//                             <Box sx={{ textAlign: 'right', pt: 0.5 }}> {/* Align better */}
//                                 <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                     Last Scan On:
//                                 </Typography>
//                                 <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Medium' }}>
//                                     {formatDate(displayResult.updatedAt)} {/* Or createdAt */}
//                                 </Typography>
//                             </Box>
//                         </Box>

//                         {/* === Card Body: Passed Checks Section === */}
//                         <Box sx={{ bgcolor: '#e8f5e9' }}> {/* Section Header Background */}
//                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, pl: 2.5 }}>
//                                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.dark' }} />
//                                  <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="success.darker" /* Darker shade */ >
//                                     Passed Checks
//                                  </Typography>
//                              </Box>
//                         </Box>
//                         {/* Passed Checks List */}
//                         <List disablePadding sx={{ bgcolor: 'background.paper' }}>
//                              {displayResult.passedChecks.length > 0 ? (
//                                 displayResult.passedChecks.map((check, index) => (
//                                     <ListItem key={`pass-${check.checkId}-${index}`} divider sx={{ py: 1.5, px: 2.5 }}>
//                                         <ListItemText
//                                             primary={
//                                                 <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'Gilroy-Medium' }}>
//                                                     {check.checkId}
//                                                 </Typography>
//                                             }
//                                             secondary={
//                                                 <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                                     {check.checkName}
//                                                     {/* Optionally add resource: ` | Resource: ${check.resource}` */}
//                                                 </Typography>
//                                             }
//                                             primaryTypographyProps={{ variant: 'body2' }}
//                                             secondaryTypographyProps={{ variant: 'body2' }}
//                                         />
//                                     </ListItem>
//                                 ))
//                              ) : (
//                                  <ListItem sx={{ py: 1.5, px: 2.5 }}>
//                                      <ListItemText primary="No passed checks in this scan." primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontFamily: 'Gilroy-Regular' }} />
//                                  </ListItem>
//                              )}
//                         </List>

//                         {/* === Card Body: Failed Checks Section === */}
//                         {displayResult.failedChecks.length > 0 && ( // Only show section if there are failures
//                             <>
//                                 <Box sx={{ bgcolor: '#ffebee' }}> {/* Section Header Background */}
//                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, pl: 2.5 }}>
//                                          <CancelIcon sx={{ fontSize: 20, color: 'error.dark' }} />
//                                          <Typography fontFamily="Gilroy-SemiBold" fontSize="14px" color="error.darker">
//                                             Failed Checks
//                                          </Typography>
//                                      </Box>
//                                 </Box>
//                                 {/* Failed Checks List */}
//                                 <List disablePadding sx={{ bgcolor: 'background.paper' }}>
//                                     {displayResult.failedChecks.map((check, index) => (
//                                         <ListItem key={`fail-${check.checkId}-${index}`} divider sx={{ py: 1.5, px: 2.5 }}>
//                                             <ListItemText
//                                                 primary={
//                                                     <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'Gilroy-Medium' }}>
//                                                         {check.checkId}
//                                                     </Typography>
//                                                 }
//                                                 secondary={
//                                                      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                                                          {check.checkName}
//                                                          {/* Optionally add severity/resource: ` | Severity: ${check.severity || 'N/A'} | Resource: ${check.resource}` */}
//                                                      </Typography>
//                                                 }
//                                                 primaryTypographyProps={{ variant: 'body2' }}
//                                                 secondaryTypographyProps={{ variant: 'body2' }}
//                                             />
//                                         </ListItem>
//                                     ))}
//                                 </List>
//                             </>
//                         )}
//                           {/* Optional: Add section for Skipped/Parsing Errors if needed */}

//                     </Paper>
//                 )}

//                  {!isLoading && !error && !displayResult && (
//                      // --- Case where API returns ok but empty data array ---
//                      // This is now handled by the `error` state set in useEffect, but kept here as a fallback visual
//                       <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid #E0E0E0", textAlign: 'center' }}>
//                           <Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}>
//                               No Checkov scan data is available to display.
//                           </Typography>
//                       </Paper>
//                   )}

//             </Box> {/* End of Centered Content Wrapper */}
//         </Container>
//     );
// };

// export default CheckovDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState } from "react";
// import {
// 	Autocomplete,
// 	Box,
// 	Breadcrumbs,
// 	Button,
// 	Container,
// 	Divider,
// 	Grid,
// 	IconButton,
// 	Link,
// 	TextField,
// 	Typography,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { ErrorMessage, Form, Formik, getIn } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";
// import MainDrawer from "../MainDrawer";

// const CheckovDetails: React.FC =  () => {
//     return(<Box sx={{ display: "flex"}}>
//         {/* <MainDrawer index={"aisolutions-rag"} /> */}
    
//         <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100vh", width:"100vw"}}>
//      <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography>
//         </Box>
//         </Box>)
    
//     }
    
// export default CheckovDetails;
    